import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
    hiddenFieldsFromInputs,
    type FormBuilderData,
    useCreateFormFromDraft,
    useDeleteBuilderBlock,
    useDeleteBuilderInput,
    useDeletePage,
    useFormBuilder,
    useUpdateForm,
    useUpsertBuilderBlock,
    useUpsertBuilderInput,
    useUpsertPage,
} from "@/features/forms/api/formBuilderApi";
import type { Database, FormViewT, InputT } from "@patient-watch/types";
import NumberFlowValue from "@number-flow/react";
import {
    MarkdownFormatHint,
    NumberFlow,
    formHasPageLogic,
    pageLogicToJson,
    RatingOptions,
    resolveNextPageId,
    RichLabel,
    type FormAnswerMap,
    type PageLogic,
} from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { cn } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { ScrollArea } from "@patient-watch/ui/scroll-area";
import { Separator } from "@patient-watch/ui/separator";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "@patient-watch/ui/select";
import { Switch } from "@patient-watch/ui/switch";
import { Label } from "@patient-watch/ui/label";
import { Slider } from "@patient-watch/ui/slider";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@patient-watch/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@patient-watch/ui/dialog";
import {
    AlertTriangle,
    AlignLeft,
    ArrowDown,
    ArrowUp,
    Calendar,
    CheckSquare,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    CircleDot,
    Clock,
    Copy,
    Ellipsis,
    EyeOff,
    FileText,
    Hash,
    ImageIcon,
    Languages,
    ListOrdered,
    Calculator,
    ChartLine,
    ListChecks,
    ListTree,
    Loader2,
    Mail,
    MapPin,
    Monitor,
    Plus,
    Settings,
    Settings2,
    SlidersHorizontal,
    Smartphone,
    Split,
    Star,
    ToggleLeft,
    Trash2,
    Type,
} from "lucide-react";
import { BranchingScaleOptionsInput } from "@/components/input/custom/branching-scale-options/BranchingScaleOptionsInput";
import { PageLogicEditor } from "@/features/forms/components/form-builder/PageLogicEditor";
import PainSlider from "@/components/input/custom/pain-slider/PainSlider";
import GlucoseSlider from "@/components/input/custom/glucose-slider/GlucoseSlider";
import TriedTreatment from "@/components/input/custom/heelpain/TriedTreatment";
import { BlockSwitch } from "@/features/blocks/components/BlockSwitch";
import { DictionaryChoicesPanel } from "@/features/dictionaries/components/DictionaryChoicesPanel";
import CountStepper from "@/features/inputs/components/CountStepper";
import InputSwitch from "@/features/inputs/components/InputSwitch";
import FormProgress from "@/features/pages/components/FormProgress";
import { FormInstrumentTitle } from "@/features/pages/components/FormInstrumentTitle";
import Title from "@/components/common/Title";
import { formInstructionsPlacement, trimmedField } from "@/features/pages/utils/formChrome";
import Call from "@/components/common/Call";
import { toVideoEmbedUrl } from "@/features/blocks/utils/toVideoEmbedUrl";
import { uploadFormMedia, validateFormMediaFile } from "@/features/blocks/utils/uploadFormMedia";
import {
    EMPTY_FORMULA,
    isNumericInputKey,
    formatFormulaResult,
    previewHiddenFieldValue,
    validateCustomFormula,
    type HiddenFieldFormula,
    type HiddenFieldFormulaMode,
} from "./hiddenFieldFormula";
import {
    useAutoSaveStatus,
    flushAutoSaves,
    retryAutoSaves,
    useBuilderAutoSave,
    type AutoSaveStatus,
} from "./useAutoSave";
import { ALLOWED_FORM_DRAFT_INPUT_KEYS, formDraftInputSchema, type FormDraft } from "@/features/ai-builder/formDraft";
import { formBuilderDataToDraft } from "@/features/ai-builder/formRevision";
import { FormAiAssistant } from "@/features/ai-builder/FormAiAssistant";
import { FormPublicationControls } from "./FormPublicationControls";
import { FormCalculatorSelect } from "./FormCalculatorSelect";
import { FormulaEditor, pillTone } from "./FormulaEditor";
import { FormBuilderWorkspaceProvider, useFormBuilderWorkspace } from "./formBuilderWorkspace";
import { useNavigate } from "@tanstack/react-router";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { resolveFormBuilderSelection, type FormBuilderSearch } from "./formBuilderSearch";
import { FormChartSettings } from "./FormChartSettings";
import { OptionsFromEditor, ShowWhenEditor, useEarlierQuestions } from "./QuestionConditionFields";
import { PageValidationEditor } from "./PageValidationEditor";
import type { PageValidationRule } from "@patient-watch/forms";
import { FormLanguageSettings } from "./FormLanguageSettings";
import { applyFormTranslations, type SupportedLocale } from "@patient-watch/i18n";
import { useFormTranslations } from "@/features/forms/api/formTranslationsBuilderApi";
import { BuilderStepPreview } from "./BuilderStepPreview";
import { FormScreenerSettings } from "@/features/screeners/components/FormScreenerSettings";
import { useFormScreener } from "@/features/screeners/api/screenerBuilderApi";
import { useAuth } from "@/utils/auth";

type InputKeys = Database["public"]["Enums"]["input_keys"];
type OrientationKeys = Database["public"]["Enums"]["orientation_keys"];
type ScoringKeys = Database["public"]["Enums"]["scoring_keys"];
type PrevalenceKeys = Database["public"]["Enums"]["prevalence_keys"];
type MedicalSpecialtyKeys = Database["public"]["Enums"]["medical_specialty_keys"];
type InputRow = Database["public"]["Tables"]["inputs"]["Row"];
type InputInsert = Database["public"]["Tables"]["inputs"]["Insert"];
type PageRow = Database["public"]["Tables"]["pages"]["Row"];
interface BlockRow {
    block_id: string;
    page_id: string;
    block_index: number;
    block_type: "text" | "image" | "video";
    content: string | null;
    media_url: string | null;
    caption: string | null;
}
interface BlockInsert {
    block_id?: string;
    page_id: string;
    block_index?: number;
    block_type: "text" | "image" | "video";
    content?: string | null;
    media_url?: string | null;
    caption?: string | null;
}

interface ScaleOption {
    label: string;
    value: number;
    description?: string | null;
    segments?: { label: string; description?: string | null }[] | null;
}

function BuilderSaveStatus({ formId }: { formId: string }) {
    const workspace = useFormBuilderWorkspace();
    const autoSaveStatus = useAutoSaveStatus();
    const mutations = useIsMutating({ mutationKey: ["form-builder-save", formId] });
    if (workspace) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-muted-foreground text-xs">
                <span className="bg-slate-400 rounded-full w-1.5 h-1.5" />
                Not saved
            </span>
        );
    }
    if (autoSaveStatus === "error") {
        return (
            <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-destructive/40 rounded-full text-destructive text-xs"
                role="alert"
            >
                <span className="bg-destructive rounded-full w-1.5 h-1.5" />
                Not saved
                <ui.Button type="button" size="sm" variant="outline" className="h-6" onClick={() => retryAutoSaves()}>
                    Retry
                </ui.Button>
            </span>
        );
    }
    const saving = autoSaveStatus === "saving" || mutations > 0;
    const label = saving ? "Saving…" : autoSaveStatus === "unsaved" ? "Unsaved changes" : "Saved";
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-muted-foreground text-xs"
            aria-live="polite"
        >
            <span
                className={cn(
                    "rounded-full w-1.5 h-1.5",
                    saving || autoSaveStatus === "unsaved" ? "bg-amber-500 animate-pulse" : "bg-green-500"
                )}
            />
            {label}
        </span>
    );
}

function AutoSaveIndicator({
    status,
    error,
    onRetry,
}: {
    status: AutoSaveStatus;
    error?: unknown;
    onRetry: () => void;
}) {
    const label = status === "saving" ? "Saving…" : status === "unsaved" ? "Unsaved" : "Saved";
    if (status === "error") {
        return (
            <div className="flex items-center gap-2 text-destructive text-xs" role="alert">
                <span>{error instanceof Error ? error.message : "Save failed"}</span>
                <ui.Button type="button" size="sm" variant="outline" className="h-7" onClick={onRetry}>
                    Retry
                </ui.Button>
            </div>
        );
    }
    return (
        <p className="text-muted-foreground text-xs" aria-live="polite">
            {label}
        </p>
    );
}

// ── Input type metadata ──────────────────────────────────────

const INPUT_TYPE_CONFIG: Record<string, { label: string; icon: typeof Type; category: string }> = {
    "scale-rating": {
        label: "Scale Rating",
        icon: ListOrdered,
        category: "Choice",
    },
    "single-choice": {
        label: "Single Choice",
        icon: CircleDot,
        category: "Choice",
    },
    "multiple-choice": {
        label: "Multiple Choice",
        icon: CheckSquare,
        category: "Choice",
    },
    "yes-no": { label: "Yes / No", icon: ToggleLeft, category: "Choice" },
    checkbox: { label: "Checkbox", icon: CheckSquare, category: "Choice" },
    dropdown: { label: "Dropdown", icon: ChevronDown, category: "Choice" },
    "short-text": { label: "Short Text", icon: Type, category: "Text" },
    "long-text": { label: "Long Text", icon: AlignLeft, category: "Text" },
    email: { label: "Email", icon: Mail, category: "Text" },
    "address-autocomplete": { label: "Address lookup", icon: MapPin, category: "Text" },
    number: { label: "Number", icon: Hash, category: "Numeric" },
    count: { label: "Count (plus and minus)", icon: Hash, category: "Numeric" },
    slider: { label: "Slider", icon: SlidersHorizontal, category: "Numeric" },
    "pain-slider": {
        label: "Pain Slider",
        icon: SlidersHorizontal,
        category: "Numeric",
    },
    "glucose-slider": {
        label: "Glucose Slider",
        icon: SlidersHorizontal,
        category: "Numeric",
    },
    star: { label: "Star Rating", icon: Star, category: "Numeric" },
    "date-picker": {
        label: "Date Picker",
        icon: Calendar,
        category: "Date & Time",
    },
    "date-time-picker": {
        label: "Date & Time",
        icon: Calendar,
        category: "Date & Time",
    },
    "month-year": {
        label: "Month & Year",
        icon: Calendar,
        category: "Date & Time",
    },
    duration: { label: "Duration", icon: Clock, category: "Date & Time" },
    photo: { label: "Photo", icon: ImageIcon, category: "Media" },
    image: { label: "Image", icon: ImageIcon, category: "Media" },
    file: { label: "File Upload", icon: FileText, category: "Media" },
    "foot-photos": { label: "Foot Photos", icon: ImageIcon, category: "Media" },
    "repeatable-group": {
        label: "Repeatable Group",
        icon: Copy,
        category: "Special",
    },
    "ratings-list": {
        label: "Ratings List",
        icon: ListOrdered,
        category: "Special",
    },
    "tried-treatment": {
        label: "Tried Treatment",
        icon: CheckSquare,
        category: "Special",
    },
    warning: {
        label: "Warning / Info",
        icon: AlertTriangle,
        category: "Special",
    },
    "visa-a-8": { label: "VISA-A Q8", icon: FileText, category: "Specialised" },
    "visa-g-8": { label: "VISA-G Q8", icon: FileText, category: "Specialised" },
    "visa-h-8": { label: "VISA-H Q8", icon: FileText, category: "Specialised" },
    "visa-p-8": { label: "VISA-P Q8", icon: FileText, category: "Specialised" },
};

function getTypeConfig(key: string) {
    return INPUT_TYPE_CONFIG[key] ?? { label: key, icon: FileText, category: "Other" };
}

// ── Scale Options Editor ─────────────────────────────────────

function ScaleOptionsEditor({
    options,
    onChange,
}: {
    options: ScaleOption[];
    onChange: (options: ScaleOption[]) => void;
}) {
    return (
        <div className="space-y-2">
            <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Scale Options</Label>
            {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                    <div className="flex justify-center items-center bg-muted rounded-full w-6 h-6 shrink-0">
                        <span className="font-medium text-[10px]">{option.value}</span>
                    </div>
                    <ui.Input
                        value={option.label}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = [...options];
                            next[index] = { ...option, label: e.target.value };
                            onChange(next);
                        }}
                        placeholder="Label"
                        className="flex-1 h-8 text-sm"
                    />
                    <ui.Input
                        type="number"
                        value={option.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = [...options];
                            next[index] = {
                                ...option,
                                value: Number(e.target.value),
                            };
                            onChange(next);
                        }}
                        className="w-16 h-8 text-sm"
                    />
                    <ui.Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => onChange(options.filter((_, i) => i !== index))}
                    >
                        <Trash2 size={20} />
                    </ui.Button>
                </div>
            ))}
            <ui.Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs"
                onClick={() =>
                    onChange([
                        ...options,
                        {
                            label: "",
                            value: options.length,
                        },
                    ])
                }
            >
                <Plus size={20} className="mr-1" /> Add Option
            </ui.Button>
        </div>
    );
}

// ── String Options Editor ────────────────────────────────────

function StringOptionsEditor({ options, onChange }: { options: string[]; onChange: (options: string[]) => void }) {
    return (
        <div className="space-y-2">
            <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Options</Label>
            {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                    <div className="flex justify-center items-center bg-muted rounded-full w-6 h-6 shrink-0">
                        <span className="font-medium text-[10px]">{index + 1}</span>
                    </div>
                    <ui.Input
                        value={option}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = [...options];
                            next[index] = e.target.value;
                            onChange(next);
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 h-8 text-sm"
                    />
                    <ui.Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => onChange(options.filter((_, i) => i !== index))}
                    >
                        <Trash2 size={20} />
                    </ui.Button>
                </div>
            ))}
            <ui.Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs"
                onClick={() => onChange([...options, ""])}
            >
                <Plus size={20} className="mr-1" /> Add Option
            </ui.Button>
        </div>
    );
}

// ── Input Type Selector ──────────────────────────────────────

function InputTypeSelector({ value, onChange }: { value: string; onChange: (value: InputKeys) => void }) {
    const grouped = Object.entries(INPUT_TYPE_CONFIG).reduce(
        (acc, [key, config]) => {
            const list = acc[config.category] ?? [];
            list.push({ key, ...config });
            acc[config.category] = list;
            return acc;
        },
        {} as Record<string, { key: string; label: string }[]>
    );

    return (
        <Select value={value} onValueChange={(v) => onChange(v as InputKeys)}>
            <SelectTrigger className="h-8 text-sm">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {Object.entries(grouped).map(([category, items]) => (
                    <SelectGroup key={category}>
                        <SelectLabel>{category}</SelectLabel>
                        {items.map((item) => (
                            <SelectItem key={item.key} value={item.key}>
                                {item.label}
                            </SelectItem>
                        ))}
                    </SelectGroup>
                ))}
            </SelectContent>
        </Select>
    );
}

// ── Custom input types that render their own Card/label ──────

const CUSTOM_INPUT_KEYS = new Set([
    "visa-a-8",
    "visa-g-8",
    "visa-h-8",
    "visa-p-8",
    "pain-slider",
    "glucose-slider",
    "tried-treatment",
]);

function CustomInputPreview({ input }: { input: InputInsert }) {
    const methods = useForm({ defaultValues: { preview: null } });

    const component = (() => {
        switch (input.input_key) {
            case "visa-a-8":
            case "visa-g-8":
            case "visa-h-8":
            case "visa-p-8":
                return <BranchingScaleOptionsInput input={input} name="preview" />;
            case "pain-slider":
                return (
                    <PainSlider name="preview" label={input.input_label ?? undefined} required={!input.is_optional} />
                );
            case "glucose-slider":
                return (
                    <GlucoseSlider
                        name="preview"
                        label={input.input_label ?? undefined}
                        required={!input.is_optional}
                    />
                );
            case "tried-treatment":
                return <TriedTreatment name="preview" label={input.input_label ?? ""} required={!input.is_optional} />;
            default:
                return null;
        }
    })();

    return <FormProvider {...methods}>{component}</FormProvider>;
}

// ══════════════════════════════════════════════════════════════
// CENTRE PANEL — LIVE PREVIEW
// ══════════════════════════════════════════════════════════════

function BuilderPreview({ input, compact = false }: { input: InputInsert; compact?: boolean }) {
    if (CUSTOM_INPUT_KEYS.has(input.input_key)) {
        return (
            <div
                className={cn(
                    "flex flex-col justify-center items-center",
                    compact ? "p-5" : "px-8 pt-[max(0.5rem,calc(2rem-5vh))] pb-8 h-full"
                )}
            >
                <div className="w-full max-w-lg">
                    <CustomInputPreview key={`${input.input_id}-${input.input_key}`} input={input} />
                </div>
            </div>
        );
    }

    const scaleOptions: ScaleOption[] = Array.isArray(input.scale_options)
        ? (input.scale_options as unknown as ScaleOption[])
        : [];
    const stringOptions: string[] = input.options ?? [];
    const orientation = input.orientation ?? "vertical";

    return (
        <div
            className={cn(
                "flex flex-col justify-center items-center",
                compact ? "p-5" : "px-8 pt-[max(0.5rem,calc(2rem-5vh))] pb-8 h-full"
            )}
        >
            <div className={cn("bg-white border rounded-xl w-full max-w-lg", !compact && "shadow-lg rounded-2xl")}>
                <div className="space-y-4 p-6">
                    <p className="font-semibold text-slate-800 text-lg leading-snug">
                        <RichLabel>{input.input_label || "Untitled Question"}</RichLabel>
                    </p>
                    {input.input_description ? (
                        <p className="text-slate-500 text-sm">
                            <RichLabel>{input.input_description}</RichLabel>
                        </p>
                    ) : null}
                    <div className="pt-2">
                        <InteractivePreview
                            key={`${input.input_id}-${input.input_key}`}
                            inputKey={input.input_key}
                            scaleOptions={scaleOptions}
                            stringOptions={stringOptions}
                            orientation={orientation}
                            minValue={input.min_value ?? 0}
                            maxValue={input.max_value ?? 10}
                            unit={input.unit ?? null}
                            decimalPoints={input.decimal_points ?? null}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function InteractivePreview({
    inputKey,
    scaleOptions,
    stringOptions,
    orientation,
    minValue,
    maxValue,
    unit,
    decimalPoints,
}: {
    inputKey: string;
    scaleOptions: ScaleOption[];
    stringOptions: string[];
    orientation: string;
    minValue: number;
    maxValue: number;
    unit: string | null;
    decimalPoints: number | null;
}) {
    const [stringValue, setStringValue] = useState<string | null>(null);
    const [numberValue, setNumberValue] = useState<string>("");
    const [multiValue, setMultiValue] = useState<string[]>([]);
    const [sliderValue, setSliderValue] = useState<number[]>([Math.round((minValue + maxValue) / 2)]);
    const [starValue, setStarValue] = useState<number>(0);
    const [checkboxValue, setCheckboxValue] = useState(false);

    const ratingOptions = scaleOptions.map((o) => ({
        value: String(o.value),
        label: o.label,
        description: o.description,
        segments: o.segments ?? undefined,
    }));

    switch (inputKey) {
        // ── Scale Rating — uses the real RatingOptions component ──
        case "scale-rating":
            return (
                <RatingOptions
                    options={ratingOptions}
                    orientation={orientation as "vertical" | "horizontal"}
                    value={stringValue}
                    onChange={setStringValue}
                    name="preview-rating"
                />
            );

        // ── Single Choice ────────────────────────────────────
        case "single-choice":
            return (
                <ui.RadioGroup value={stringValue ?? ""} onValueChange={setStringValue}>
                    <div className="space-y-2">
                        {stringOptions.map((opt, i) => (
                            <label
                                key={i}
                                className={cn(
                                    "flex items-center gap-3 p-2.5 rounded-lg transition-colors cursor-pointer",
                                    stringValue === opt ? "bg-indigo-50" : "hover:bg-slate-50"
                                )}
                            >
                                <ui.RadioGroupItem value={opt} />
                                <span className="text-slate-700 text-sm">{opt || `Option ${i + 1}`}</span>
                            </label>
                        ))}
                    </div>
                </ui.RadioGroup>
            );

        // ── Multiple Choice ──────────────────────────────────
        case "multiple-choice":
            return (
                <div className="space-y-2">
                    {stringOptions.map((opt, i) => {
                        const checked = multiValue.includes(opt);
                        return (
                            <label
                                key={i}
                                className={cn(
                                    "flex items-center gap-3 p-2.5 rounded-lg transition-colors cursor-pointer",
                                    checked ? "bg-indigo-50" : "hover:bg-slate-50"
                                )}
                            >
                                <ui.Checkbox
                                    checked={checked}
                                    onCheckedChange={(c) => {
                                        if (c) {
                                            setMultiValue((prev) => [...prev, opt]);
                                        } else {
                                            setMultiValue((prev) => prev.filter((v) => v !== opt));
                                        }
                                    }}
                                />
                                <span className="text-slate-700 text-sm">{opt || `Option ${i + 1}`}</span>
                            </label>
                        );
                    })}
                </div>
            );

        // ── Dropdown ─────────────────────────────────────────
        case "dropdown":
            return (
                <Select value={stringValue ?? ""} onValueChange={setStringValue}>
                    <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Select an option..." />
                    </SelectTrigger>
                    <SelectContent>
                        {stringOptions.map((opt, i) => (
                            <SelectItem key={i} value={opt || `option-${i}`}>
                                {opt || `Option ${i + 1}`}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );

        // ── Yes / No ─────────────────────────────────────────
        case "yes-no":
            return (
                <ui.RadioGroup
                    value={stringValue ?? ""}
                    onValueChange={setStringValue}
                    className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg w-fit"
                >
                    <div className="flex items-center gap-2">
                        <ui.RadioGroupItem value="yes" />
                        <span className="text-slate-700 text-sm">Yes</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <ui.RadioGroupItem value="no" />
                        <span className="text-slate-700 text-sm">No</span>
                    </div>
                </ui.RadioGroup>
            );

        // ── Checkbox ─────────────────────────────────────────
        case "checkbox":
            return (
                <div className="flex items-center gap-2 cursor-pointer">
                    <ui.Checkbox checked={checkboxValue} onCheckedChange={(c) => setCheckboxValue(!!c)} />
                    <span className="text-slate-700 text-sm">Check to confirm</span>
                </div>
            );

        // ── Sliders ──────────────────────────────────────────
        case "slider": {
            const sorted = [...scaleOptions].toSorted((a, b) => b.value - a.value);
            const lowLabel = sorted.length > 0 ? sorted[sorted.length - 1]?.label : String(minValue);
            const highLabel = sorted.length > 0 ? sorted[0]?.label : String(maxValue);
            return (
                <div className="space-y-3">
                    <div className="font-semibold text-slate-700 text-2xl text-center">{sliderValue[0]}</div>
                    <Slider value={sliderValue} onValueChange={setSliderValue} min={minValue} max={maxValue} step={1} />
                    <div className="flex justify-between text-slate-400 text-xs">
                        <span>{lowLabel}</span>
                        <span>{highLabel}</span>
                    </div>
                </div>
            );
        }

        // ── Star ─────────────────────────────────────────────
        case "star":
            return (
                <div className="flex gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <button key={i} type="button" onClick={() => setStarValue(i + 1)} className="transition-colors">
                            <Star
                                className={cn(
                                    "w-8 h-8 transition-colors",
                                    i < starValue ? "text-amber-400 fill-amber-400" : "text-slate-300"
                                )}
                            />
                        </button>
                    ))}
                </div>
            );

        // ── Count ────────────────────────────────────────────
        case "count": {
            const counted = numberValue.trim() === "" ? null : Number(numberValue);
            return (
                <CountStepper
                    value={counted !== null && Number.isFinite(counted) ? counted : null}
                    min={minValue}
                    max={maxValue}
                    label="Count"
                    onChange={(next) => setNumberValue(next === null ? "" : String(next))}
                />
            );
        }

        // ── Number ───────────────────────────────────────────
        case "number":
            return (
                <div className="flex items-center gap-2">
                    <ui.Input
                        type="number"
                        value={numberValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumberValue(e.target.value)}
                        placeholder={decimalPoints ? `0.${"0".repeat(decimalPoints)}` : "0"}
                        step={decimalPoints ? (1 / 10 ** decimalPoints).toString() : "1"}
                        className="max-w-[140px] h-12 text-lg"
                    />
                    {unit && <span className="font-medium text-slate-500 text-sm">{unit}</span>}
                </div>
            );

        // ── Text ─────────────────────────────────────────────
        case "short-text":
        case "address-autocomplete":
            return (
                <ui.Input
                    value={numberValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumberValue(e.target.value)}
                    placeholder="Type your answer..."
                />
            );
        case "long-text":
            return (
                <ui.Textarea
                    value={numberValue}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNumberValue(e.target.value)}
                    placeholder="Type your answer..."
                    rows={3}
                />
            );
        case "email":
            return (
                <ui.Input
                    type="email"
                    value={numberValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumberValue(e.target.value)}
                    placeholder="name@example.com"
                />
            );

        // ── Date / Time ──────────────────────────────────────
        case "date-picker":
        case "date-time-picker":
        case "month-year":
            return (
                <ui.Input
                    type={inputKey === "date-time-picker" ? "datetime-local" : "date"}
                    value={numberValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNumberValue(e.target.value)}
                    className="max-w-[220px]"
                />
            );
        case "duration":
            return (
                <div className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg text-slate-400 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>Enter duration...</span>
                </div>
            );

        // ── Warning ──────────────────────────────────────────
        case "warning":
            return (
                <div className="flex items-start gap-3 bg-amber-50 p-3 border border-amber-200 rounded-lg">
                    <AlertTriangle className="mt-0.5 w-5 h-5 text-amber-500 shrink-0" />
                    <span className="text-amber-800 text-sm">This is an informational notice.</span>
                </div>
            );

        // ── Media ────────────────────────────────────────────
        case "photo":
        case "image":
        case "file":
        case "foot-photos":
            return (
                <div className="flex flex-col items-center gap-2 p-6 border-2 border-slate-200 border-dashed rounded-lg text-slate-400">
                    <ImageIcon className="w-8 h-8" />
                    <span className="text-sm">Upload file</span>
                </div>
            );

        // ── Ratings List ─────────────────────────────────────
        case "ratings-list":
            return (
                <div className="space-y-3">
                    {[1, 2, 3].map((n) => (
                        <div key={n} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg">
                            <span className="text-slate-500 text-sm">Activity {n}</span>
                            <Slider defaultValue={[5]} min={0} max={10} step={1} className="w-32" />
                        </div>
                    ))}
                </div>
            );

        default:
            return <p className="text-slate-400 text-sm">Preview not available for this type.</p>;
    }
}

// ══════════════════════════════════════════════════════════════
// RIGHT PANEL — CONFIG (only relevant fields)
// ══════════════════════════════════════════════════════════════

// Questions deleted from the row must not be written back by the open editor's auto-save.
const suppressedQuestionSaves = new Set<string>();

function InputConfigPanel({
    input,
    form,
    formId,
    onDeleted,
    onEditChange,
}: {
    input: InputRow;
    form: FormBuilderData;
    formId: string;
    onDeleted: () => void;
    onEditChange: (edited: InputInsert) => void;
}) {
    const upsertInput = useUpsertBuilderInput(formId);
    const deleteInput = useDeleteBuilderInput(formId);
    const [edited, setEdited] = useState<InputInsert>({ ...input });

    useEffect(() => {
        setEdited({ ...input });
    }, [input.input_id]);

    // Push live edits up so the preview stays in sync
    useEffect(() => {
        onEditChange(edited);
    }, [edited, onEditChange]);

    const scaleOptions: ScaleOption[] = Array.isArray(edited.scale_options)
        ? (edited.scale_options as unknown as ScaleOption[])
        : [];

    const autoSave = useBuilderAutoSave(edited, async (next) => {
        if (next.input_id && suppressedQuestionSaves.has(next.input_id)) return;
        // A dictionary supplies the choices, so the draft rule that asks for options does not apply.
        if (
            next.dictionary_id == null &&
            ALLOWED_FORM_DRAFT_INPUT_KEYS.includes(next.input_key as (typeof ALLOWED_FORM_DRAFT_INPUT_KEYS)[number])
        ) {
            const result = formDraftInputSchema.safeParse({
                input_key: next.input_key,
                input_label: next.input_label ?? "",
                input_description: next.input_description ?? undefined,
                is_optional: next.is_optional ?? false,
                options: next.options ?? undefined,
                scale_options: next.scale_options ?? undefined,
                min_value: next.min_value ?? undefined,
                max_value: next.max_value ?? undefined,
            });
            if (!result.success) throw new Error(result.error.issues[0]?.message ?? "Review this question.");
        }
        // Position is owned by the row move buttons. Saving it here puts a moved question back.
        const { input_index: _index, created_at: _created, updated_at: _updated, ...rest } = next;
        await upsertInput.mutateAsync(rest);
    });
    const handleDelete = () => {
        if (input.input_id) suppressedQuestionSaves.add(input.input_id);
        deleteInput.mutate(input.input_id, { onSuccess: onDeleted });
    };

    const needsScaleOptions = edited.input_key === "scale-rating";
    const earlierQuestions = useEarlierQuestions(form, input.page_id, input.input_id);
    const isChoiceInput = ["single-choice", "multiple-choice", "dropdown"].includes(edited.input_key);

    const isDictionaryBound = edited.dictionary_id != null;

    const needsDictionaryPicker =
        isDictionaryBound ||
        ["single-choice", "multiple-choice", "dropdown", "scale-rating"].includes(edited.input_key);

    const needsSliderConfig = edited.input_key === "slider";

    const needsStringOptions = ["single-choice", "multiple-choice", "dropdown"].includes(edited.input_key);

    const needsOrientation = ["scale-rating", "single-choice", "multiple-choice", "slider"].includes(edited.input_key);

    const needsMinMax = ["number", "slider", "count"].includes(edited.input_key);

    const needsUnitAndDecimals = edited.input_key === "number";

    const needsStep = ["number", "slider", "measurement"].includes(edited.input_key);

    const needsMaxLength = ["short-text", "long-text", "address-autocomplete"].includes(edited.input_key);

    const needsInputList = edited.input_key === "repeatable-group";

    return (
        <ScrollArea className="h-full">
            <div className="space-y-5 p-4">
                {/* Type */}
                <div className="space-y-1.5">
                    <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Type</Label>
                    <InputTypeSelector
                        value={edited.input_key}
                        onChange={(v) => setEdited((p) => ({ ...p, input_key: v }))}
                    />
                </div>

                {/* Label */}
                <div className="space-y-1.5">
                    <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Question
                    </Label>
                    <ui.Textarea
                        value={edited.input_label ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setEdited((p) => ({
                                ...p,
                                input_label: e.target.value,
                            }))
                        }
                        placeholder="Enter the question text..."
                        rows={2}
                        className="text-sm resize-y"
                    />
                    <MarkdownFormatHint extra="The live preview on the right shows how patients see it." />
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                    <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Description
                    </Label>
                    <ui.Textarea
                        value={edited.input_description ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setEdited((p) => ({
                                ...p,
                                input_description: e.target.value || null,
                            }))
                        }
                        placeholder="Helper text shown below the question..."
                        rows={2}
                        className="text-sm resize-y"
                    />
                    <MarkdownFormatHint extra="The live preview on the right shows how patients see it." />
                </div>

                {/* ── Type-specific config ─────────────────── */}

                {needsDictionaryPicker && (
                    <>
                        <Separator />
                        <DictionaryChoicesPanel
                            input={edited as never}
                            onChange={(dictionaryId) => setEdited((p) => ({ ...p, dictionary_id: dictionaryId }))}
                        />
                    </>
                )}

                {needsScaleOptions && !isDictionaryBound && (
                    <>
                        <Separator />
                        <ScaleOptionsEditor
                            options={scaleOptions}
                            onChange={(opts) =>
                                setEdited((p) => ({
                                    ...p,
                                    scale_options: opts as unknown as InputInsert["scale_options"],
                                }))
                            }
                        />
                    </>
                )}

                {needsSliderConfig && (
                    <>
                        <Separator />
                        <div className="space-y-3">
                            <div className="gap-3 grid grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label className="text-muted-foreground text-xs">Min</Label>
                                    <ui.Input
                                        type="number"
                                        value={edited.min_value ?? ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setEdited((p) => ({
                                                ...p,
                                                min_value: e.target.value === "" ? null : Number(e.target.value),
                                            }))
                                        }
                                        className="h-8 text-sm"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-muted-foreground text-xs">Max</Label>
                                    <ui.Input
                                        type="number"
                                        value={edited.max_value ?? ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setEdited((p) => ({
                                                ...p,
                                                max_value: e.target.value === "" ? null : Number(e.target.value),
                                            }))
                                        }
                                        className="h-8 text-sm"
                                    />
                                </div>
                            </div>
                            <ScaleOptionsEditor
                                options={scaleOptions}
                                onChange={(opts) =>
                                    setEdited((p) => ({
                                        ...p,
                                        scale_options: opts as unknown as InputInsert["scale_options"],
                                    }))
                                }
                            />
                        </div>
                    </>
                )}

                {needsStringOptions && !isDictionaryBound && (
                    <>
                        <Separator />
                        <StringOptionsEditor
                            options={edited.options ?? []}
                            onChange={(opts) => setEdited((p) => ({ ...p, options: opts }))}
                        />
                    </>
                )}

                {needsMinMax && !needsSliderConfig && (
                    <>
                        <Separator />
                        <div className="gap-3 grid grid-cols-2">
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Min Value</Label>
                                <ui.Input
                                    type="number"
                                    value={edited.min_value ?? ""}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEdited((p) => ({
                                            ...p,
                                            min_value: e.target.value === "" ? null : Number(e.target.value),
                                        }))
                                    }
                                    className="h-8 text-sm"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Max Value</Label>
                                <ui.Input
                                    type="number"
                                    value={edited.max_value ?? ""}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEdited((p) => ({
                                            ...p,
                                            max_value: e.target.value === "" ? null : Number(e.target.value),
                                        }))
                                    }
                                    className="h-8 text-sm"
                                />
                            </div>
                        </div>
                    </>
                )}

                {needsStep && (
                    <>
                        <Separator />
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">Step</Label>
                            <ui.Input
                                type="number"
                                min={0}
                                step="any"
                                value={edited.step ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const next = e.target.value === "" ? null : Number(e.target.value);
                                    setEdited((p) => ({ ...p, step: next !== null && next > 0 ? next : null }));
                                }}
                                placeholder={edited.input_key === "measurement" ? "0.1" : "1"}
                                className="h-8 text-sm"
                            />
                            <p className="text-2xs text-muted-foreground">
                                How far one nudge moves, for example 0.1, 0.101, 1, 5 or 10. Leave empty for the
                                default.
                            </p>
                        </div>
                    </>
                )}

                {needsUnitAndDecimals && (
                    <>
                        <Separator />
                        <div className="gap-3 grid grid-cols-2">
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Unit</Label>
                                <ui.Input
                                    value={edited.unit ?? ""}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEdited((p) => ({
                                            ...p,
                                            unit: e.target.value || null,
                                        }))
                                    }
                                    placeholder="e.g. %, mL, mm"
                                    className="h-8 text-sm"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Decimal Places</Label>
                                <ui.Input
                                    type="number"
                                    min={0}
                                    max={10}
                                    value={edited.decimal_points ?? ""}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEdited((p) => ({
                                            ...p,
                                            decimal_points: e.target.value === "" ? null : Number(e.target.value),
                                        }))
                                    }
                                    placeholder="0 (integer)"
                                    className="h-8 text-sm"
                                />
                            </div>
                        </div>
                    </>
                )}

                {needsMaxLength && (
                    <>
                        <Separator />
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">Max Length</Label>
                            <ui.Input
                                type="number"
                                value={edited.max_length ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setEdited((p) => ({
                                        ...p,
                                        max_length: e.target.value === "" ? null : Number(e.target.value),
                                    }))
                                }
                                placeholder="No limit"
                                className="h-8 text-sm"
                            />
                        </div>
                    </>
                )}

                {needsInputList && (
                    <>
                        <Separator />
                        <StringOptionsEditor
                            options={edited.input_list ?? []}
                            onChange={(opts) => setEdited((p) => ({ ...p, input_list: opts }))}
                        />
                    </>
                )}

                {needsOrientation && (
                    <>
                        <Separator />
                        <div className="space-y-1.5">
                            <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                                Orientation
                            </Label>
                            <Select
                                value={edited.orientation ?? "vertical"}
                                onValueChange={(v) =>
                                    setEdited((p) => ({
                                        ...p,
                                        orientation: v as OrientationKeys,
                                    }))
                                }
                            >
                                <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="vertical">Vertical</SelectItem>
                                    <SelectItem value="horizontal">Horizontal</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}

                {edited.input_key === "count" && (
                    <div className="gap-3 grid grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">Lowest count</Label>
                            <ui.Input
                                type="number"
                                value={edited.min_count ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setEdited((p) => ({
                                        ...p,
                                        min_count: e.target.value ? Number(e.target.value) : null,
                                    }))
                                }
                                className="h-8 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">Highest count</Label>
                            <ui.Input
                                type="number"
                                value={edited.max_count ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setEdited((p) => ({
                                        ...p,
                                        max_count: e.target.value ? Number(e.target.value) : null,
                                    }))
                                }
                                className="h-8 text-sm"
                            />
                        </div>
                    </div>
                )}

                {/* ── Common settings ─────────────────────── */}
                <Separator />
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs" htmlFor={`input-caption-${input.input_id}`}>
                            Hint
                        </Label>
                        <ui.Input
                            id={`input-caption-${input.input_id}`}
                            value={edited.input_caption ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((p) => ({ ...p, input_caption: e.target.value || null }))
                            }
                            placeholder="Short help shown under the question"
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label
                            className="text-muted-foreground text-xs"
                            htmlFor={`validation-message-${input.input_id}`}
                        >
                            Error message
                        </Label>
                        <ui.Input
                            id={`validation-message-${input.input_id}`}
                            value={edited.validation_message ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((p) => ({ ...p, validation_message: e.target.value || null }))
                            }
                            placeholder="Shown when the answer is missing or invalid"
                            className="h-8 text-sm"
                        />
                    </div>
                    {(
                        [
                            ["is_optional", "Optional", "Patients can skip this question."],
                            ["is_editable", "Editable later", "Patients can change the answer after saving."],
                            ["carry_forward", "Carry forward", "Start from the patient's last answer."],
                            ...(edited.input_key === "dropdown"
                                ? [["allow_multiple", "Pick more than one", "Patients can choose several."]]
                                : []),
                        ] as ["is_optional" | "is_editable" | "carry_forward" | "allow_multiple", string, string][]
                    ).map(([column, label, help]) => (
                        <div key={column} className="flex justify-between items-center gap-3">
                            <div>
                                <Label htmlFor={`${column}-${input.input_id}`} className="text-sm">
                                    {label}
                                </Label>
                                <p className="text-muted-foreground text-xs">{help}</p>
                            </div>
                            <Switch
                                id={`${column}-${input.input_id}`}
                                checked={edited[column] === true}
                                onCheckedChange={(v) => setEdited((p) => ({ ...p, [column]: v }))}
                            />
                        </div>
                    ))}
                </div>

                <Separator />
                <ShowWhenEditor
                    questions={earlierQuestions}
                    value={edited.show_when}
                    onChange={(condition) => setEdited((p) => ({ ...p, show_when: (condition ?? null) as never }))}
                />

                <Separator />
                <AdvancedFields>
                    {isChoiceInput ? (
                        <OptionsFromEditor
                            questions={earlierQuestions}
                            value={edited.options_from}
                            onChange={(entries) =>
                                setEdited((p) => ({ ...p, options_from: (entries ?? null) as never }))
                            }
                        />
                    ) : null}
                    <div className="gap-3 grid grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs" htmlFor={`subscale-${input.input_id}`}>
                                Subscale
                            </Label>
                            <ui.Input
                                id={`subscale-${input.input_id}`}
                                value={edited.subscale ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setEdited((p) => ({ ...p, subscale: e.target.value || null }))
                                }
                                placeholder="e.g. womac:pain"
                                className="h-8 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs" htmlFor={`score-target-${input.input_id}`}>
                                Score target
                            </Label>
                            <ui.Input
                                id={`score-target-${input.input_id}`}
                                value={edited.score_target ?? ""}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setEdited((p) => ({ ...p, score_target: e.target.value || null }))
                                }
                                placeholder="e.g. womac:total"
                                className="h-8 text-sm"
                            />
                        </div>
                    </div>
                    <p className="text-muted-foreground text-xs">
                        Subscale and score target tie this question to a calculator. Most forms leave them blank.
                    </p>
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs" htmlFor={`input-oid-${input.input_id}`}>
                            Input OID
                        </Label>
                        <ui.Input
                            id={`input-oid-${input.input_id}`}
                            value={edited.input_oid ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((p) => ({ ...p, input_oid: e.target.value || null }))
                            }
                            placeholder="Leave blank if unused"
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs" htmlFor={`sas-label-${input.input_id}`}>
                            SAS label
                        </Label>
                        <ui.Input
                            id={`sas-label-${input.input_id}`}
                            value={edited.sas_label ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((p) => ({ ...p, sas_label: e.target.value || null }))
                            }
                            placeholder="Leave blank if unused"
                            className="h-8 text-sm"
                        />
                        <p className="text-muted-foreground text-xs">
                            Registry identifiers for this question. Patients do not see these.
                        </p>
                    </div>
                </AdvancedFields>

                {/* ── Actions ─────────────────────────────── */}
                <Separator />
                <div className="flex justify-between items-center gap-2">
                    <AutoSaveIndicator status={autoSave.status} error={autoSave.error} onRetry={autoSave.retry} />
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <ui.Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                aria-label="Delete question"
                            >
                                <Trash2 size={20} />
                            </ui.Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this question?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This cannot be undone. The question will be permanently removed.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel asChild>
                                    <fc.CancelButton size="sm" className="w-auto" />
                                </AlertDialogCancel>
                                <AlertDialogAction asChild>
                                    <fc.DeleteButton size="sm" onClick={handleDelete} className="w-auto" />
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </ScrollArea>
    );
}

function BlockConfigPanel({ block, formId, onDeleted }: { block: BlockRow; formId: string; onDeleted: () => void }) {
    const upsertBlock = useUpsertBuilderBlock(formId);
    const deleteBlock = useDeleteBuilderBlock(formId);
    const [edited, setEdited] = useState<BlockInsert>({ ...block });
    const [uploadState, setUploadState] = useState<{ status: "idle" | "uploading" | "error"; message: string | null }>({
        status: "idle",
        message: null,
    });

    useEffect(() => {
        setEdited({ ...block });
    }, [block.block_id]);

    const autoSave = useBuilderAutoSave(edited, async (next) => {
        const isText = next.block_type === "text";
        const content = isText ? next.content?.trim() || null : null;
        let mediaUrl = isText ? null : next.media_url?.trim() || null;
        if (next.block_type === "video" && mediaUrl) {
            mediaUrl = toVideoEmbedUrl(mediaUrl);
        }

        if (isText && !content) {
            throw new Error("Text blocks need content.");
        }
        if (!isText && !mediaUrl) {
            throw new Error("Image and video blocks need a media URL.");
        }

        await upsertBlock.mutateAsync({
            block_id: block.block_id,
            page_id: block.page_id,
            block_index: next.block_index,
            block_type: next.block_type,
            content,
            media_url: mediaUrl,
            caption: next.caption ?? null,
        });
    });
    const handleDelete = () => deleteBlock.mutate(block.block_id, { onSuccess: onDeleted });

    return (
        <ScrollArea className="h-full">
            <div className="space-y-5 p-4">
                <div className="space-y-1.5">
                    <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Content type
                    </Label>
                    <Select
                        value={edited.block_type}
                        onValueChange={(v) => {
                            const nextType = v as "text" | "image" | "video";
                            setEdited((prev) =>
                                nextType === "text"
                                    ? { ...prev, block_type: nextType, media_url: null, content: prev.content ?? "" }
                                    : {
                                          ...prev,
                                          block_type: nextType,
                                          content: null,
                                          media_url: prev.media_url ?? "",
                                      }
                            );
                        }}
                    >
                        <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="image">Image</SelectItem>
                            <SelectItem value="video">Video</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {edited.block_type === "text" ? (
                    <div className="space-y-1.5">
                        <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                            Content
                        </Label>
                        <ui.Textarea
                            value={edited.content ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setEdited((prev) => ({ ...prev, content: e.target.value }))
                            }
                            rows={4}
                            placeholder="Enter text content..."
                            className="text-sm resize-y"
                        />
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                            Media URL
                        </Label>
                        <ui.Input
                            value={edited.media_url ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((prev) => ({ ...prev, media_url: e.target.value }))
                            }
                            placeholder={
                                edited.block_type === "video"
                                    ? "YouTube/Vimeo URL or direct video URL..."
                                    : "Image URL..."
                            }
                            className="h-8 text-sm"
                        />
                        <div className="space-y-1.5">
                            <Label className="text-muted-foreground text-xs">
                                Upload {edited.block_type === "video" ? "video" : "picture"} to Supabase storage
                            </Label>
                            <ui.Input
                                type="file"
                                accept={edited.block_type === "video" ? "video/*" : "image/*"}
                                aria-label={`Upload ${edited.block_type} file`}
                                disabled={uploadState.status === "uploading"}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const file = e.target.files?.[0];
                                    if (!file || edited.block_type === "text") return;
                                    const validation = validateFormMediaFile(file, edited.block_type);
                                    if (validation) {
                                        setUploadState({ status: "error", message: validation });
                                        return;
                                    }
                                    setUploadState({ status: "uploading", message: "Uploading…" });
                                    void uploadFormMedia({ formId, blockId: block.block_id, file })
                                        .then(({ url }) => {
                                            setEdited((prev) => ({ ...prev, media_url: url }));
                                            setUploadState({ status: "idle", message: "Uploaded to storage." });
                                        })
                                        .catch((caught) =>
                                            setUploadState({
                                                status: "error",
                                                message: caught instanceof Error ? caught.message : "Upload failed.",
                                            })
                                        );
                                }}
                                className="h-8 text-sm"
                            />
                            {uploadState.message ? (
                                <p
                                    className={
                                        uploadState.status === "error"
                                            ? "text-destructive text-xs"
                                            : "text-muted-foreground text-xs"
                                    }
                                    role={uploadState.status === "error" ? "alert" : "status"}
                                >
                                    {uploadState.message}
                                </p>
                            ) : null}
                        </div>
                        {edited.block_type === "video" ? (
                            <p className="text-muted-foreground text-xs">
                                Paste a normal YouTube link - we convert it to an embed URL on save.
                            </p>
                        ) : null}
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        Caption
                    </Label>
                    <ui.Input
                        value={edited.caption ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEdited((prev) => ({ ...prev, caption: e.target.value || null }))
                        }
                        className="h-8 text-sm"
                    />
                </div>

                <Separator />
                <div className="flex justify-between items-center gap-2">
                    <AutoSaveIndicator status={autoSave.status} error={autoSave.error} onRetry={autoSave.retry} />
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <ui.Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                aria-label="Delete content"
                            >
                                <Trash2 size={20} />
                            </ui.Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this content?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This content will be removed from the page.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel asChild>
                                    <fc.CancelButton size="sm" className="w-auto" />
                                </AlertDialogCancel>
                                <AlertDialogAction asChild>
                                    <fc.DeleteButton size="sm" onClick={handleDelete} className="w-auto" />
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </ScrollArea>
    );
}

function PageDetailsEditor({ page, form, formId }: { page: PageRow; form: FormBuilderData; formId: string }) {
    const upsertPage = useUpsertPage(formId);
    const initial = () => ({
        page_label: page.page_label ?? "",
        page_description: page.page_description ?? "",
        // Kept as written while editing: half-finished rules are saved, and the form ignores them until complete.
        rules: (Array.isArray((page.page_validation as { rules?: unknown } | null)?.rules)
            ? (page.page_validation as unknown as { rules: PageValidationRule[] }).rules
            : []) as PageValidationRule[],
    });
    const [edited, setEdited] = useState(initial);

    useEffect(() => {
        setEdited(initial());
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the page changes
    }, [page.page_id]);

    const earlierQuestions = useEarlierQuestions(form, page.page_id, undefined);
    const pageInputIds = new Set(
        (form.pages.find((candidate) => candidate.page_id === page.page_id)?.inputs ?? []).map(
            (input) => input.input_id
        )
    );
    const pageQuestions = earlierQuestions.filter((question) => pageInputIds.has(question.input_id));

    const autoSave = useBuilderAutoSave(edited, async (next) => {
        if (!next.page_label.trim()) throw new Error("The page needs a title.");
        await upsertPage.mutateAsync({
            page_id: page.page_id,
            form_id: formId,
            page_label: next.page_label,
            page_description: next.page_description || null,
            page_index: page.page_index,
            page_validation: next.rules.length ? ({ rules: next.rules } as never) : null,
        });
    });

    return (
        <div className="space-y-4 p-5 border-b">
            <div className="space-y-1">
                <Label htmlFor={`page-title-${page.page_id}`} className="text-xs">
                    Page title
                </Label>
                <ui.Input
                    id={`page-title-${page.page_id}`}
                    value={edited.page_label}
                    className="h-9 text-sm"
                    onChange={(event) => setEdited((current) => ({ ...current, page_label: event.target.value }))}
                />
            </div>
            <div className="space-y-1">
                <Label htmlFor={`page-description-${page.page_id}`} className="text-xs">
                    Page description
                </Label>
                <ui.Textarea
                    id={`page-description-${page.page_id}`}
                    value={edited.page_description}
                    rows={2}
                    className="text-sm"
                    onChange={(event) => setEdited((current) => ({ ...current, page_description: event.target.value }))}
                />
            </div>
            <PageValidationEditor
                rules={edited.rules}
                pageQuestions={pageQuestions}
                earlierQuestions={earlierQuestions}
                onChange={(rules) => setEdited((current) => ({ ...current, rules }))}
            />
            <AutoSaveIndicator status={autoSave.status} error={autoSave.error} onRetry={autoSave.retry} />
        </div>
    );
}

// ══════════════════════════════════════════════════════════════
// FORM SETTINGS SHEET
// ══════════════════════════════════════════════════════════════

const SCORING_OPTIONS: { value: ScoringKeys; label: string }[] = [
    { value: "higher_better", label: "Higher is Better" },
    { value: "lower_better", label: "Lower is Better" },
];
const PREVALENCE_OPTIONS: { value: PrevalenceKeys; label: string }[] = [
    { value: "ubiquitous", label: "Ubiquitous" },
    { value: "common", label: "Common" },
    { value: "uncommon", label: "Uncommon" },
    { value: "niche", label: "Niche" },
];
const MEDICAL_SPECIALTIES: { value: MedicalSpecialtyKeys; label: string }[] = [
    { value: "orthopaedics", label: "Orthopaedics" },
    { value: "sports_medicine", label: "Sports Medicine" },
    { value: "pain_medicine", label: "Pain Medicine" },
    { value: "rheumatology", label: "Rheumatology" },
    { value: "urology", label: "Urology" },
    { value: "neurology", label: "Neurology" },
    { value: "cardiology", label: "Cardiology" },
    { value: "general_practice", label: "General Practice" },
    { value: "physiotherapy", label: "Physiotherapy" },
    { value: "psychiatry", label: "Psychiatry" },
    { value: "dermatology", label: "Dermatology" },
    { value: "other", label: "Other" },
];

function AdvancedFields({ children }: { children: ReactNode }) {
    return (
        <ui.Collapsible>
            <ui.CollapsibleTrigger className="group flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm">
                <ChevronRight size={14} className="group-data-[state=open]:rotate-90 transition-transform" />
                Advanced
            </ui.CollapsibleTrigger>
            <ui.CollapsibleContent className="space-y-3 pt-3">{children}</ui.CollapsibleContent>
        </ui.Collapsible>
    );
}

function FormSettingsSheet({ form, formId }: { form: FormBuilderData; formId: string }) {
    const updateForm = useUpdateForm(formId);
    const [edited, setEdited] = useState({
        form_name: form.form_name ?? "",
        form_caption: form.form_caption ?? "",
        form_kicker: form.form_kicker ?? "",
        copyright_info: form.copyright_info ?? "",
        form_description: form.form_description ?? "",
        form_instructions: form.form_instructions ?? "",
        instructions_before_start: form.instructions_before_start ?? false,
        prevalence: form.prevalence,
        time_to_complete: form.time_to_complete,
        slug: form.slug ?? "",
        medical_specialties: form.medical_specialties ?? [],
        indications: form.indications ?? [],
        orthopaedic_areas: form.orthopaedic_areas ?? [],
        form_oid: form.form_oid ?? "",
    });

    useEffect(() => {
        setEdited({
            form_name: form.form_name ?? "",
            form_caption: form.form_caption ?? "",
            form_kicker: form.form_kicker ?? "",
            copyright_info: form.copyright_info ?? "",
            form_description: form.form_description ?? "",
            form_instructions: form.form_instructions ?? "",
            instructions_before_start: form.instructions_before_start ?? false,
            prevalence: form.prevalence,
            time_to_complete: form.time_to_complete,
            slug: form.slug ?? "",
            medical_specialties: form.medical_specialties ?? [],
            indications: form.indications ?? [],
            orthopaedic_areas: form.orthopaedic_areas ?? [],
            form_oid: form.form_oid ?? "",
        });
    }, [form.form_id]);

    const autoSave = useBuilderAutoSave(edited, async (next) => {
        if (!next.form_name.trim()) throw new Error("The form needs a name.");
        await updateForm.mutateAsync({
            form_name: next.form_name,
            form_caption: next.form_caption.trim() || null,
            form_kicker: next.form_kicker.trim() || null,
            copyright_info: next.copyright_info.trim() || null,
            form_description: next.form_description || null,
            form_instructions: next.form_instructions || null,
            instructions_before_start: next.instructions_before_start,
            prevalence: next.prevalence,
            time_to_complete: next.time_to_complete,
            slug: next.slug || null,
            medical_specialties: next.medical_specialties.length ? next.medical_specialties : null,
            indications: next.indications.length ? next.indications : null,
            orthopaedic_areas: next.orthopaedic_areas.length ? next.orthopaedic_areas : null,
            form_oid: next.form_oid.trim() || null,
        });
    });

    return (
        <ScrollArea className="flex-1 overflow-auto">
            <div className="space-y-5 p-6">
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Form Name</Label>
                    <ui.Input
                        value={edited.form_name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEdited((p) => ({
                                ...p,
                                form_name: e.target.value,
                            }))
                        }
                        className="text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Caption</Label>
                    <ui.Textarea
                        value={edited.form_caption}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setEdited((p) => ({ ...p, form_caption: e.target.value }))
                        }
                        rows={2}
                        placeholder="Shown above the title on the cover and every page"
                        className="text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Kicker</Label>
                    <ui.Input
                        value={edited.form_kicker}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEdited((p) => ({ ...p, form_kicker: e.target.value }))
                        }
                        placeholder="A short line above the caption, such as the hospital name"
                        className="text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Copyright</Label>
                    <ui.Textarea
                        value={edited.copyright_info}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setEdited((p) => ({ ...p, copyright_info: e.target.value }))
                        }
                        rows={2}
                        placeholder="Licence notice for validated questionnaires"
                        className="text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Slug</Label>
                    <ui.Input
                        value={edited.slug}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setEdited((p) => ({ ...p, slug: e.target.value }))
                        }
                        className="text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Description</Label>
                    <ui.Textarea
                        value={edited.form_description}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setEdited((p) => ({
                                ...p,
                                form_description: e.target.value,
                            }))
                        }
                        rows={3}
                        className="text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Instructions</Label>
                    <ui.Textarea
                        value={edited.form_instructions}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setEdited((p) => ({
                                ...p,
                                form_instructions: e.target.value,
                            }))
                        }
                        rows={3}
                        className="text-sm"
                    />
                </div>
                <div className="flex justify-between items-start gap-3">
                    <div className="space-y-0.5">
                        <Label className="text-sm" htmlFor="form-instructions-before-start">
                            Show instructions before the first question
                        </Label>
                        <p className="text-muted-foreground text-xs">
                            Use for long instructions. They appear once on their own screen with a Start button instead
                            of above every question.
                        </p>
                    </div>
                    <Switch
                        id="form-instructions-before-start"
                        checked={edited.instructions_before_start}
                        disabled={!edited.form_instructions.trim()}
                        onCheckedChange={(checked) => setEdited((p) => ({ ...p, instructions_before_start: checked }))}
                    />
                </div>

                <Separator />

                <div className="gap-3 grid grid-cols-2">
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs">Prevalence</Label>
                        <Select
                            value={edited.prevalence ?? ""}
                            onValueChange={(v) =>
                                setEdited((p) => ({
                                    ...p,
                                    prevalence: v as PrevalenceKeys,
                                }))
                            }
                        >
                            <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                                {PREVALENCE_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs">Time (min)</Label>
                        <ui.Input
                            type="number"
                            value={edited.time_to_complete ?? ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((p) => ({
                                    ...p,
                                    time_to_complete: e.target.value === "" ? null : Number(e.target.value),
                                }))
                            }
                            className="h-8 text-sm"
                        />
                    </div>
                </div>

                <Separator />

                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Medical Specialties</Label>
                    <div className="flex flex-wrap gap-1.5">
                        {MEDICAL_SPECIALTIES.map((s) => {
                            const on = edited.medical_specialties?.includes(s.value);
                            return (
                                <Badge
                                    key={s.value}
                                    variant={on ? "default" : "outline"}
                                    className="text-xs cursor-pointer"
                                    onClick={() =>
                                        setEdited((p) => ({
                                            ...p,
                                            medical_specialties: on
                                                ? p.medical_specialties.filter((x) => x !== s.value)
                                                : [...p.medical_specialties, s.value],
                                        }))
                                    }
                                >
                                    {s.label}
                                </Badge>
                            );
                        })}
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Indications</Label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {edited.indications.map((ind, i) => (
                            <Badge
                                key={i}
                                variant="secondary"
                                className="text-xs cursor-pointer"
                                onClick={() =>
                                    setEdited((p) => ({
                                        ...p,
                                        indications: p.indications.filter((_, idx) => idx !== i),
                                    }))
                                }
                            >
                                {ind} &times;
                            </Badge>
                        ))}
                    </div>
                    <ui.Input
                        placeholder="Type and press Enter..."
                        className="h-8 text-sm"
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") {
                                const val = (e.target as HTMLInputElement).value.trim();
                                if (val) {
                                    setEdited((p) => ({
                                        ...p,
                                        indications: [...p.indications, val],
                                    }));
                                    (e.target as HTMLInputElement).value = "";
                                }
                            }
                        }}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Anatomic Areas</Label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {edited.orthopaedic_areas.map((area, i) => (
                            <Badge
                                key={i}
                                variant="secondary"
                                className="text-xs cursor-pointer"
                                onClick={() =>
                                    setEdited((p) => ({
                                        ...p,
                                        orthopaedic_areas: p.orthopaedic_areas.filter((_, idx) => idx !== i),
                                    }))
                                }
                            >
                                {area} &times;
                            </Badge>
                        ))}
                    </div>
                    <ui.Input
                        placeholder="Type and press Enter..."
                        className="h-8 text-sm"
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") {
                                const val = (e.target as HTMLInputElement).value.trim();
                                if (val) {
                                    setEdited((p) => ({
                                        ...p,
                                        orthopaedic_areas: [...p.orthopaedic_areas, val],
                                    }));
                                    (e.target as HTMLInputElement).value = "";
                                }
                            }
                        }}
                    />
                </div>

                <Separator />

                <AdvancedFields>
                    <div className="space-y-1.5">
                        <Label className="text-muted-foreground text-xs" htmlFor="form-oid">
                            Form OID
                        </Label>
                        <ui.Input
                            id="form-oid"
                            value={edited.form_oid}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEdited((p) => ({ ...p, form_oid: e.target.value }))
                            }
                            placeholder="Leave blank if unused"
                            className="h-8 text-sm"
                        />
                        <p className="text-muted-foreground text-xs">
                            Stable identifier for registry export. Patients do not see this.
                        </p>
                    </div>
                </AdvancedFields>

                <AutoSaveIndicator status={autoSave.status} error={autoSave.error} onRetry={autoSave.retry} />
            </div>
        </ScrollArea>
    );
}

type BuilderPage = FormBuilderData["pages"][number];

function getPageItems(page: BuilderPage) {
    return [
        ...page.inputs.map((input) => ({
            kind: "input" as const,
            id: input.input_id,
            index: input.input_index,
            label: input.input_label || "Untitled question",
            input,
        })),
        ...page.blocks.map((block) => ({
            kind: "block" as const,
            id: block.block_id,
            index: block.block_index,
            label: block.caption || `${block.block_type} block`,
            block,
        })),
    ].sort((a, b) => {
        if (a.index !== b.index) return a.index - b.index;
        if (a.kind === b.kind) return a.id.localeCompare(b.id);
        return a.kind === "block" ? -1 : 1;
    });
}

function PagePreview({
    page,
    selectedInputId,
    selectedBlockId,
    liveEdited,
    onSelect,
}: {
    page: BuilderPage | null;
    selectedInputId?: string;
    selectedBlockId?: string;
    liveEdited: InputInsert | null;
    onSelect: SelectionChange;
}) {
    if (!page) {
        return (
            <div className="flex flex-col justify-center items-center p-8 h-full text-center">
                <div className="flex justify-center items-center bg-muted mb-4 rounded-full w-16 h-16">
                    <ListOrdered size={20} className="text-muted-foreground" />
                </div>
                <h3 className="mb-1 font-medium text-lg">Add the first page</h3>
                <p className="max-w-xs text-muted-foreground text-sm">
                    Pages organise the questions and content patients see.
                </p>
            </div>
        );
    }

    const items = getPageItems(page);
    return (
        <ScrollArea className="bg-muted/30 h-full">
            <div className="space-y-5 mx-auto p-5 lg:p-8 max-w-2xl">
                <header className="space-y-1 pb-5 border-b">
                    <p className="font-medium text-muted-foreground text-xs">Page {page.page_index}</p>
                    <h2 className="font-semibold text-2xl tracking-tight">
                        <RichLabel>{page.page_label ?? "Untitled page"}</RichLabel>
                    </h2>
                    {page.page_description ? (
                        <p className="text-muted-foreground text-sm">
                            <RichLabel>{page.page_description}</RichLabel>
                        </p>
                    ) : null}
                </header>

                {items.length === 0 ? (
                    <div className="bg-background p-10 border border-dashed rounded-lg text-muted-foreground text-sm text-center">
                        This page has no questions or content blocks yet.
                    </div>
                ) : (
                    items.map((item) => {
                        const selected =
                            item.kind === "input" ? item.id === selectedInputId : item.id === selectedBlockId;
                        return (
                            <section
                                key={item.id}
                                className={cn(
                                    "relative bg-background border rounded-xl",
                                    selected && "border-primary ring-1 ring-primary/30"
                                )}
                            >
                                <ui.Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="top-2 right-2 z-10 absolute bg-background/90 h-7 text-xs"
                                    aria-label={`Edit ${item.label}`}
                                    onClick={() =>
                                        onSelect(
                                            item.kind === "input"
                                                ? { pageId: page.page_id, inputId: item.id }
                                                : { pageId: page.page_id, blockId: item.id }
                                        )
                                    }
                                >
                                    Edit
                                </ui.Button>
                                {item.kind === "input" ? (
                                    <BuilderPreview
                                        compact
                                        input={
                                            item.id === selectedInputId && liveEdited ? liveEdited : { ...item.input }
                                        }
                                    />
                                ) : (
                                    <div className="p-6 pt-12">
                                        <BlockSwitch block={item.block} />
                                    </div>
                                )}
                            </section>
                        );
                    })
                )}
            </div>
        </ScrollArea>
    );
}

function SelectionInspector({
    form,
    page,
    input,
    block,
    readOnly,
    onDeleted,
    onEditChange,
    onSavePageLogic,
    onDeletePage,
}: {
    form: FormBuilderData;
    page: BuilderPage | null;
    input: InputRow | null;
    block: BlockRow | null;
    readOnly: boolean;
    onDeleted: () => void;
    onEditChange: (input: InputInsert) => void;
    onSavePageLogic: (logic: PageLogic | null) => Promise<unknown>;
    onDeletePage: () => void;
}) {
    if (!page) return null;

    return (
        <fieldset disabled={readOnly} className="contents">
            {readOnly ? (
                <p className="bg-muted/50 px-5 py-2 border-b text-muted-foreground text-xs">
                    Published and licensed forms are read-only.
                </p>
            ) : null}
            {input ? (
                <InputConfigPanel
                    key={input.input_id}
                    input={input}
                    form={form}
                    formId={form.form_id}
                    onDeleted={onDeleted}
                    onEditChange={onEditChange}
                />
            ) : block ? (
                <BlockConfigPanel key={block.block_id} block={block} formId={form.form_id} onDeleted={onDeleted} />
            ) : (
                <ScrollArea className="h-full">
                    <div className="px-5 py-4 border-b">
                        <p className="font-semibold text-sm">Page settings</p>
                        <p className="text-muted-foreground text-xs">Edit page details and patient branching.</p>
                    </div>
                    <PageDetailsEditor key={page.page_id} page={page} form={form} formId={form.form_id} />
                    <div className="p-5">
                        <PageLogicEditor
                            form={form}
                            pageId={page.page_id}
                            pageLogic={page.page_logic}
                            onSave={onSavePageLogic}
                        />
                    </div>
                    <div className="p-5 border-t">
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <ui.Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                >
                                    <Trash2 size={16} /> Delete page
                                </ui.Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete page?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will delete the page and all its questions and blocks.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel asChild>
                                        <fc.CancelButton size="sm" className="w-auto" />
                                    </AlertDialogCancel>
                                    <AlertDialogAction asChild>
                                        <fc.DeleteButton size="sm" onClick={onDeletePage} className="w-auto" />
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </ScrollArea>
            )}
        </fieldset>
    );
}

// ══════════════════════════════════════════════════════════════
// MAIN FORM BUILDER
// ══════════════════════════════════════════════════════════════

type SelectionChange = (search: FormBuilderSearch, options?: { replace?: boolean }) => void;

export default function FormBuilder({
    formId,
    initialDraft,
    search = {},
    onSelectionChange = () => undefined,
    tab,
    onTabChange,
}: {
    formId?: string;
    initialDraft?: FormDraft;
    search?: FormBuilderSearch;
    onSelectionChange?: SelectionChange;
    /** The step to show. When set, the route owns it; otherwise the builder keeps its own. */
    tab?: BuilderTab;
    onTabChange?: (tab: BuilderTab) => void;
}) {
    if (initialDraft) {
        return (
            <FormBuilderWorkspaceProvider initialDraft={initialDraft}>
                <FormBuilderCanvas
                    search={search}
                    onSelectionChange={onSelectionChange}
                    tab={tab}
                    onTabChange={onTabChange}
                />
            </FormBuilderWorkspaceProvider>
        );
    }
    return (
        <PersistedFormBuilder
            formId={formId!}
            search={search}
            onSelectionChange={onSelectionChange}
            tab={tab}
            onTabChange={onTabChange}
        />
    );
}

function PersistedFormBuilder({
    formId,
    ...props
}: {
    formId: string;
    search: FormBuilderSearch;
    onSelectionChange: SelectionChange;
    tab?: BuilderTab;
    onTabChange?: (tab: BuilderTab) => void;
}) {
    const { data: form } = useFormBuilder(formId);
    return <FormBuilderCanvas form={form} {...props} />;
}

// ══════════════════════════════════════════════════════════════
// FORMBRICKS-STYLE LAYOUT (Questions / Language / Settings - no Styling)
// Pages map to Blocks, inputs + blocks map to Questions.
// ══════════════════════════════════════════════════════════════

export const BUILDER_TABS = ["questions", "calculator", "chart", "screener", "language", "settings"] as const;
export type BuilderTab = (typeof BUILDER_TABS)[number];

/** Optional steps a form can switch off. Off steps stay clickable but are greyed out. */
type BuilderOptionalTab = "calculator" | "screener" | "chart";

function BuilderTopTabs({
    active,
    onChange,
    enabled,
}: {
    active: BuilderTab;
    onChange: (tab: BuilderTab) => void;
    enabled: Record<BuilderOptionalTab, boolean>;
}) {
    const tabs: { id: BuilderTab; label: string; icon: typeof ListTree }[] = [
        { id: "questions", label: "Questions", icon: ListTree },
        { id: "calculator", label: "Calculator", icon: Calculator },
        { id: "chart", label: "Chart", icon: ChartLine },
        { id: "screener", label: "Screener", icon: ListChecks },
        { id: "language", label: "Language", icon: Languages },
        { id: "settings", label: "Settings", icon: Settings2 },
    ];
    return (
        <nav aria-label="Builder sections" className="flex items-center gap-6">
            {tabs.map((tab, idx) => {
                const isOff = tab.id in enabled && !enabled[tab.id as BuilderOptionalTab];
                return (
                    <div key={tab.id} className="flex items-center gap-6">
                        <button
                            type="button"
                            onClick={() => onChange(tab.id)}
                            aria-current={active === tab.id ? "page" : undefined}
                            title={isOff ? `${tab.label} is off` : undefined}
                            data-state={isOff ? "off" : "on"}
                            className={cn(
                                "flex items-center gap-1.5 pb-2 border-b-2 text-sm transition-colors",
                                active === tab.id
                                    ? "border-teal-500 font-medium text-foreground"
                                    : "border-transparent text-muted-foreground hover:text-foreground",
                                isOff && "opacity-50"
                            )}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                            {isOff ? <span className="sr-only"> (off)</span> : null}
                        </button>
                        {idx < tabs.length - 1 ? <ChevronRight size={14} className="text-muted-foreground/50" /> : null}
                    </div>
                );
            })}
        </nav>
    );
}

/** Placeholder for a builder step that is not built yet. */
function BuilderComingSoonSection({ title, description }: { title: string; description: string }) {
    return (
        <section className="bg-background p-6 border rounded-xl">
            <h2 className="font-semibold text-sm">{title}</h2>
            <p className="mt-1 text-muted-foreground text-sm">{description}</p>
        </section>
    );
}

function HiddenScoreEditor({
    input,
    form,
    onDeleted,
}: {
    input: InputRow;
    form: FormBuilderData;
    onDeleted: () => void;
}) {
    const formId = form.form_id;
    const upsertInput = useUpsertBuilderInput(formId);
    const deleteInput = useDeleteBuilderInput(formId);
    const [edited, setEdited] = useState<InputInsert>({ ...input, page_id: null, input_key: "number" });
    const [formula, setFormula] = useState<HiddenFieldFormula>(EMPTY_FORMULA);

    useEffect(() => {
        setEdited({ ...input, page_id: null, input_key: "number" });
    }, [input.input_id]);

    const autoSave = useBuilderAutoSave(edited, async (next) => {
        const label = next.input_label?.trim();
        if (!label) throw new Error("Every hidden field needs a name.");
        if (next.min_value != null && next.max_value != null && next.max_value <= next.min_value) {
            throw new Error("A maximum value must be greater than its minimum.");
        }
        await upsertInput.mutateAsync({ ...next, page_id: null, input_key: "number", input_label: label });
    });

    const numberField = (key: "min_value" | "max_value" | "max_score", label: string) => (
        <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs">{label}</Label>
            <ui.Input
                type="number"
                aria-label={label}
                value={edited[key] ?? ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setEdited((current) => ({
                        ...current,
                        [key]: event.target.value === "" ? null : Number(event.target.value),
                    }))
                }
                className="h-8 text-sm"
            />
        </div>
    );

    return (
        <div className="space-y-4 p-3 border-t">
            <div className="space-y-1.5">
                <Label htmlFor={`hidden-label-${input.input_id}`} className="text-muted-foreground text-xs">
                    Score name
                </Label>
                <ui.Input
                    id={`hidden-label-${input.input_id}`}
                    value={edited.input_label ?? ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setEdited((current) => ({ ...current, input_label: event.target.value }))
                    }
                    className="h-8 text-sm"
                />
            </div>
            <div className="gap-3 grid grid-cols-3">
                {numberField("min_value", "Minimum")}
                {numberField("max_value", "Maximum")}
                {numberField("max_score", "Max score")}
            </div>
            <div className="flex justify-between items-center gap-3">
                <Label htmlFor={`hidden-primary-${input.input_id}`} className="text-sm">
                    Primary score
                </Label>
                <Switch
                    id={`hidden-primary-${input.input_id}`}
                    checked={edited.is_primary_score === true}
                    onCheckedChange={(checked) => setEdited((current) => ({ ...current, is_primary_score: checked }))}
                />
            </div>
            <div className="flex justify-between items-center gap-3">
                <Label htmlFor={`hidden-chart-${input.input_id}`} className="text-sm">
                    Show on chart
                </Label>
                <Switch
                    id={`hidden-chart-${input.input_id}`}
                    checked={edited.chart_order != null}
                    onCheckedChange={(checked) =>
                        setEdited((current) => ({
                            ...current,
                            chart_order: checked ? (current.input_index ?? 1) : null,
                        }))
                    }
                />
            </div>
            <AdvancedFields>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs" htmlFor={`hidden-oid-${input.input_id}`}>
                        Input OID
                    </Label>
                    <ui.Input
                        id={`hidden-oid-${input.input_id}`}
                        value={edited.input_oid ?? ""}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setEdited((current) => ({ ...current, input_oid: event.target.value || null }))
                        }
                        placeholder="Leave blank if unused"
                        className="h-8 text-sm"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs" htmlFor={`hidden-sas-${input.input_id}`}>
                        SAS label
                    </Label>
                    <ui.Input
                        id={`hidden-sas-${input.input_id}`}
                        value={edited.sas_label ?? ""}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setEdited((current) => ({ ...current, sas_label: event.target.value || null }))
                        }
                        placeholder="Leave blank if unused"
                        className="h-8 text-sm"
                    />
                </div>
            </AdvancedFields>
            <div className="flex justify-between items-center gap-3">
                <AutoSaveIndicator status={autoSave.status} error={autoSave.error} onRetry={autoSave.retry} />
                <ui.Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => deleteInput.mutate(input.input_id, { onSuccess: onDeleted })}
                >
                    <Trash2 size={16} className="mr-1" /> Delete
                </ui.Button>
            </div>
            <HiddenFieldFormulaSection input={input} form={form} formula={formula} onChange={setFormula} />
        </div>
    );
}

function HiddenFieldFormulaSection({
    input,
    form,
    formula,
    onChange,
}: {
    input: InputRow;
    form: FormBuilderData;
    formula: HiddenFieldFormula;
    onChange: (next: HiddenFieldFormula) => void;
}) {
    const formulaQuestions = form.pages.flatMap((page) =>
        page.inputs.map((candidate) => ({
            input_id: candidate.input_id,
            label: candidate.input_label || "Untitled question",
            pageLabel: page.page_label ?? `Page ${page.page_index}`,
            description: candidate.input_description ?? undefined,
            numeric: isNumericInputKey(candidate.input_key),
        }))
    );
    const numericInputs = formulaQuestions.filter((candidate) => candidate.numeric);
    const allowedIds = formulaQuestions.map((candidate) => candidate.input_id);
    const validation = formula.mode === "custom" ? validateCustomFormula(formula.expression, allowedIds) : null;
    const referencedIds =
        formula.mode === "custom"
            ? (validation?.references ?? [])
            : formula.mode === "sum"
              ? formula.sourceInputIds
              : [];
    const [samples, setSamples] = useState<Record<string, string>>({});
    const sampleKey = referencedIds.join(",");
    useEffect(() => {
        setSamples((current) => {
            const next = { ...current };
            referencedIds.forEach((id, index) => {
                if (next[id] == null) next[id] = String((index + 1) * 2);
            });
            return next;
        });
    }, [sampleKey]);
    const previewAnswers = Object.fromEntries(
        referencedIds.map((id) => {
            const raw = samples[id]?.trim() ?? "";
            if (!raw) return [id, null];
            const value = Number(raw);
            return [id, Number.isFinite(value) ? value : null];
        })
    );
    const preview = formula.mode === "none" ? null : previewHiddenFieldValue(formula, previewAnswers);
    const questionById = new Map(formulaQuestions.map((candidate) => [candidate.input_id, candidate]));
    const sampleForm = useForm();

    const toggleSource = (id: string) => {
        const next = formula.sourceInputIds.includes(id)
            ? formula.sourceInputIds.filter((candidate) => candidate !== id)
            : [...formula.sourceInputIds, id];
        onChange({ ...formula, sourceInputIds: next });
    };

    return (
        <div className="space-y-3 bg-muted/40 p-3 rounded-lg" data-testid={`hidden-formula-${input.input_id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-xs">Calculation</p>
                <span className="bg-amber-100 px-2 py-0.5 rounded-full text-2xs text-amber-800">
                    Work in progress - frontend only
                </span>
            </div>
            <p className="text-muted-foreground text-xs">
                Set formulas sum earlier answers. The custom calculator works like a Notion formula with + - * / ( ),
                comparisons, SUM(), AVERAGE(), MIN(), MAX(), COUNT(), ROUND(), ABS() and IF(). Pills show question names
                while operators render as icons. Database changes are still needed, so existing SQL score functions are
                untouched.
            </p>
            <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">Formula mode</Label>
                <Select
                    value={formula.mode}
                    onValueChange={(value) => onChange({ ...formula, mode: value as HiddenFieldFormulaMode })}
                >
                    <SelectTrigger aria-label="Formula mode" className="h-8 text-sm">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">No calculation</SelectItem>
                        <SelectItem value="sum">Sum earlier answers</SelectItem>
                        <SelectItem value="custom">Custom calculator</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {formula.mode === "sum" ? (
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Questions to add</Label>
                    {numericInputs.length === 0 ? (
                        <p className="text-muted-foreground text-xs">Add a number, slider, or scale question first.</p>
                    ) : (
                        <div className="space-y-1">
                            {numericInputs.map((candidate) => (
                                <label key={candidate.input_id} className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={formula.sourceInputIds.includes(candidate.input_id)}
                                        onChange={() => toggleSource(candidate.input_id)}
                                        aria-label={candidate.label}
                                    />
                                    <span className="min-w-0 truncate">
                                        {candidate.pageLabel}: {candidate.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            ) : null}
            {formula.mode === "custom" ? (
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Custom formula</Label>
                    <FormulaEditor
                        value={formula.expression}
                        onChange={(expression) => onChange({ ...formula, expression })}
                        questions={formulaQuestions.map((candidate) => ({
                            input_id: candidate.input_id,
                            label: candidate.label,
                            detail: candidate.description
                                ? `${candidate.pageLabel} - ${candidate.description}`
                                : candidate.pageLabel,
                        }))}
                        placeholder="Type a question name, SUM(…) or IF(…)"
                        ariaLabel="Custom formula"
                        testId={`hidden-formula-editor-${input.input_id}`}
                    />
                    <p className="text-2xs text-muted-foreground">
                        Type to filter questions by name or description, then pick a pill.
                    </p>
                    {validation && !validation.ok ? (
                        <p className="text-destructive text-xs" role="alert">
                            {validation.error}
                        </p>
                    ) : null}
                </div>
            ) : null}
            {referencedIds.length > 0 ? (
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Try values</Label>
                    <FormProvider {...sampleForm}>
                        {referencedIds.map((id) => {
                            const question = questionById.get(id);
                            const label = question?.label ?? id;
                            const tone = pillTone(id);
                            const raw = samples[id]?.trim() ?? "";
                            const numeric = raw !== "" && Number.isFinite(Number(raw)) ? Number(raw) : null;
                            return (
                                <div key={id} className="flex items-center gap-2">
                                    <span
                                        className="flex-1 px-2 py-1 rounded-md min-w-0 font-semibold text-xs truncate"
                                        style={{ background: tone.background, color: tone.color }}
                                    >
                                        {label}
                                    </span>
                                    <NumberFlow
                                        compact
                                        allowEmpty
                                        min={-9999}
                                        max={9999}
                                        fractionDigits={2}
                                        className="w-28 shrink-0"
                                        aria-label={`Sample value for ${label}`}
                                        value={numeric}
                                        onChange={(next) =>
                                            setSamples((current) => ({
                                                ...current,
                                                [id]: next == null ? "" : String(next),
                                            }))
                                        }
                                    />
                                </div>
                            );
                        })}
                    </FormProvider>
                </div>
            ) : null}
            {formula.mode !== "none" ? (
                <div
                    className="flex justify-between items-baseline gap-3 pt-1"
                    aria-live="polite"
                    data-testid="hidden-formula-result"
                >
                    <span className="font-bold text-foreground text-lg">Result</span>
                    {preview == null ? (
                        <span className="font-bold text-foreground text-lg">-</span>
                    ) : (
                        <NumberFlowValue
                            value={Number(formatFormulaResult(preview))}
                            format={{ maximumFractionDigits: 2 }}
                            className="font-bold tabular-nums text-foreground text-lg"
                        />
                    )}
                </div>
            ) : null}
        </div>
    );
}

/** Calculator step: how the score reads and which calculator works it out. */
function FormScoringSection({ form, formId }: { form: FormBuilderData; formId: string }) {
    const updateForm = useUpdateForm(formId);
    return (
        <section aria-label="Scoring" className="space-y-4 bg-background p-4 border rounded-xl">
            <div>
                <p className="font-medium text-sm">Scoring</p>
                <p className="text-muted-foreground text-xs">
                    Which way is better, and how the answers become a score.
                </p>
            </div>
            <div className="gap-3 grid sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Better scores are</Label>
                    <Select
                        value={form.scoring ?? ""}
                        onValueChange={(v) => updateForm.mutate({ scoring: v as ScoringKeys })}
                    >
                        <SelectTrigger aria-label="Scoring direction" className="h-8 text-sm">
                            <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                            {SCORING_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <FormCalculatorSelect form={form} />
            </div>
        </section>
    );
}

function HiddenFieldsSection({ form }: { form: FormBuilderData }) {
    const upsertInput = useUpsertBuilderInput(form.form_id);
    const hiddenInputs = [...(form.hiddenInputs ?? [])].sort((a, b) => a.input_index - b.input_index);
    const [openId, setOpenId] = useState<string | null>(null);

    const addHiddenField = () => {
        const nextIndex = hiddenInputs.length ? Math.max(...hiddenInputs.map((input) => input.input_index)) + 1 : 1;
        const id = crypto.randomUUID();
        upsertInput.mutate(
            {
                input_id: id,
                page_id: null,
                input_key: "number",
                input_label: "New score",
                input_index: nextIndex,
                min_value: 0,
                max_value: 100,
                max_score: 100,
                chart_order: null,
                is_primary_score: false,
                is_optional: true,
                is_editable: false,
                allow_multiple: false,
                orientation: "vertical",
            },
            { onSuccess: () => setOpenId(id) }
        );
    };

    return (
        <section aria-label="Hidden fields" className="bg-background border rounded-xl">
            <div className="flex items-center gap-3 p-4">
                <EyeOff size={16} className="text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Hidden fields</p>
                    <p className="text-muted-foreground text-xs">
                        Calculated scores patients do not answer, such as pain, function, and total.
                    </p>
                </div>
            </div>
            <div className="space-y-2 px-4 pb-4">
                {hiddenInputs.map((input) => {
                    const open = openId === input.input_id;
                    return (
                        <div key={input.input_id} className="border rounded-lg">
                            <button
                                type="button"
                                className="flex items-center gap-2 px-3 py-2.5 w-full text-left"
                                aria-expanded={open}
                                onClick={() => setOpenId(open ? null : input.input_id)}
                            >
                                <Hash size={16} className="text-muted-foreground shrink-0" />
                                <span className="flex-1 min-w-0 text-sm truncate">{input.input_label || "Score"}</span>
                                {input.is_primary_score ? (
                                    <span className="text-2xs text-muted-foreground shrink-0">Primary</span>
                                ) : null}
                            </button>
                            {open ? (
                                <HiddenScoreEditor
                                    key={input.input_id}
                                    input={input}
                                    form={form}
                                    onDeleted={() => setOpenId(null)}
                                />
                            ) : null}
                        </div>
                    );
                })}
                <ui.Button type="button" variant="secondary" size="sm" className="w-full" onClick={addHiddenField}>
                    <Plus size={16} className="mr-1" /> Add hidden field
                </ui.Button>
            </div>
        </section>
    );
}

function BlockCardShell({
    index,
    title,
    subtitle,
    selected,
    onSelect,
    onMoveUp,
    onMoveDown,
    onDuplicate,
    onDelete,
    canMoveUp,
    canMoveDown,
    disabled,
    children,
    containsSelection = false,
}: {
    index: number;
    title: string;
    subtitle: string;
    /** The page itself is in focus: its details are being edited. */
    selected: boolean;
    /** A question or content item on this page is in focus. */
    containsSelection?: boolean;
    onSelect: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
    canMoveUp: boolean;
    canMoveDown: boolean;
    disabled: boolean;
    children: ReactNode;
}) {
    return (
        <section
            aria-label={title}
            aria-current={selected ? "true" : undefined}
            className={cn(
                "bg-background border rounded-xl transition-colors",
                selected
                    ? "border-teal-500 ring-2 ring-teal-500/30"
                    : containsSelection
                      ? "border-teal-500/50"
                      : "hover:border-slate-300 dark:hover:border-slate-600"
            )}
        >
            <div className="flex items-center gap-2 p-4 pb-2">
                <span
                    className={cn(
                        "flex justify-center items-center border rounded-full w-6 h-6 text-xs shrink-0",
                        selected && "bg-teal-500 border-teal-500 text-white"
                    )}
                >
                    {index}
                </span>
                <button type="button" onClick={onSelect} className="flex-1 min-w-0 text-left">
                    <p className="font-medium text-sm">{title}</p>
                    <p className="text-muted-foreground text-xs">{subtitle}</p>
                </button>
                <div className="flex items-center gap-0.5 text-muted-foreground">
                    <ui.Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7"
                        aria-label={`Move ${title} up`}
                        disabled={disabled || !canMoveUp}
                        onClick={onMoveUp}
                    >
                        <ArrowUp size={16} />
                    </ui.Button>
                    <ui.Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7"
                        aria-label={`Move ${title} down`}
                        disabled={disabled || !canMoveDown}
                        onClick={onMoveDown}
                    >
                        <ArrowDown size={16} />
                    </ui.Button>
                    <ui.Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7"
                        aria-label={`Duplicate ${title}`}
                        disabled={disabled}
                        onClick={onDuplicate}
                    >
                        <Copy size={16} />
                    </ui.Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <ui.Button
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7"
                                aria-label={`Delete ${title}`}
                                disabled={disabled}
                            >
                                <Trash2 size={16} />
                            </ui.Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete {title}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will delete the page and all its questions.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
            <div className="px-4 pb-4">{children}</div>
        </section>
    );
}

function BuilderPreviewPane({
    form,
    page,
    device,
    onDeviceChange,
    onSelectPage,
    liveEdited,
    selectedInputId,
}: {
    form: FormBuilderData;
    page: BuilderPage | null;
    device: "mobile" | "desktop";
    onDeviceChange: (device: "mobile" | "desktop") => void;
    onSelectPage: (pageId: string) => void;
    liveEdited: InputInsert | null;
    selectedInputId?: string;
}) {
    // Mirrors the patient-facing FormPage and stays interactive: answers persist
    // in this form instance while Next and Back move between blocks.
    const previewMethods = useForm<Record<string, unknown>>({ defaultValues: {} });
    const [finished, setFinished] = useState(false);
    const [previewHistory, setPreviewHistory] = useState<string[]>([]);
    const previewForm = form as unknown as FormViewT;
    const pages = [...form.pages].sort((a, b) => a.page_index - b.page_index);
    const pageIndex = page ? pages.findIndex((candidate) => candidate.page_id === page.page_id) : -1;
    const items = page ? getPageItems(page) : [];
    const hideInstrumentTitle = formInstructionsPlacement(previewForm) === "intro";
    const caption = hideInstrumentTitle ? null : trimmedField(previewForm.form_caption);
    const instrumentName = hideInstrumentTitle ? null : trimmedField(previewForm.form_name);
    const kicker = hideInstrumentTitle ? null : trimmedField(previewForm.form_kicker);
    const hasInstrumentTitle = Boolean(caption || instrumentName || kicker);
    const previewPageLabel = trimmedField(page?.page_label);
    const previewPageDescription = trimmedField(page?.page_description);
    const previewInstructions =
        formInstructionsPlacement(previewForm) === "page" ? trimmedField(form.form_instructions) : null;

    // Evaluate the same page_logic the patient-facing form uses, so Next / Back
    // in the preview follow conditional branches instead of plain page order.
    const pageRefs = useMemo(
        () =>
            pages.map((candidate) => ({
                page_id: candidate.page_id,
                page_index: candidate.page_index,
                page_logic: candidate.page_logic as unknown as import("@patient-watch/forms").PageLogic | null,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- pages is rebuilt from form each render
        [form]
    );
    const hasBranching = useMemo(() => formHasPageLogic(pageRefs), [pageRefs]);
    // Re-evaluate Submit vs Next as answers change.
    const previewAnswers = previewMethods.watch() as FormAnswerMap;
    const previewIsLastPage = useMemo(() => {
        if (!page || pageIndex < 0) return pageIndex === pages.length - 1;
        if (!hasBranching) return pageIndex === pages.length - 1;
        return resolveNextPageId(page.page_id, pageRefs, previewAnswers ?? {}).kind === "end";
    }, [hasBranching, page, pageIndex, pageRefs, pages.length, previewAnswers]);

    const restart = () => {
        previewMethods.reset();
        setFinished(false);
        setPreviewHistory([]);
        const first = pages[0];
        if (first) onSelectPage(first.page_id);
    };

    const goNext = () => {
        if (!page) {
            setFinished(true);
            return;
        }
        if (!hasBranching) {
            const next = pages[pageIndex + 1];
            if (next) {
                setPreviewHistory((history) => [...history, page.page_id]);
                onSelectPage(next.page_id);
            } else setFinished(true);
            return;
        }
        const answers = (previewMethods.getValues() ?? {}) as FormAnswerMap;
        const resolution = resolveNextPageId(page.page_id, pageRefs, answers);
        if (resolution.kind === "end") {
            setFinished(true);
            return;
        }
        setPreviewHistory((history) => [...history, page.page_id]);
        onSelectPage(resolution.page_id);
    };

    const goBack = () => {
        if (finished) {
            setFinished(false);
            return;
        }
        if (hasBranching && previewHistory.length > 0) {
            const previousPageId = previewHistory[previewHistory.length - 1];
            setPreviewHistory((history) => history.slice(0, -1));
            if (previousPageId) onSelectPage(previousPageId);
            return;
        }
        const previous = pages[pageIndex - 1];
        if (previous) onSelectPage(previous.page_id);
    };

    return (
        <div className="flex flex-col bg-background border rounded-xl h-full overflow-hidden">
            <div className="flex items-center gap-2 bg-background px-4 py-2 border-b">
                <span className="bg-red-400 rounded-full w-2.5 h-2.5" />
                <span className="bg-amber-400 rounded-full w-2.5 h-2.5" />
                <span className="bg-green-400 rounded-full w-2.5 h-2.5" />
                <p className="flex-1 text-muted-foreground text-xs text-center">Preview</p>
                <ui.Button variant="ghost" size="sm" className="h-7 text-xs" onClick={restart}>
                    Restart
                </ui.Button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
                <div className={cn("mx-auto", device === "mobile" ? "max-w-xs" : "max-w-md")}>
                    {finished ? (
                        <div className="flex flex-col gap-4 bg-green-200 mx-auto p-2 px-8 py-4 rounded-lg w-fit text-green-800">
                            <p className="font-medium text-lg">Form submitted</p>
                            <ui.Button type="button" variant="outline" size="sm" onClick={restart}>
                                Restart
                            </ui.Button>
                        </div>
                    ) : page ? (
                        <>
                            <FormInstrumentTitle
                                className="mb-3"
                                variant="page"
                                caption={caption}
                                name={instrumentName}
                                kicker={kicker}
                            />
                            <FormProgress form={previewForm} pageIndex={page.page_index} />
                            {previewPageLabel || previewPageDescription ? (
                                <header className="space-y-1 mt-4 text-left">
                                    {previewPageLabel ? (
                                        hasInstrumentTitle ? (
                                            <h2 className="font-semibold text-slate-900 dark:text-slate-100 text-xl md:text-2xl tracking-tight">
                                                {previewPageLabel}
                                            </h2>
                                        ) : (
                                            <Title align="left" className="mb-0 md:mb-0" words={previewPageLabel} />
                                        )
                                    ) : null}
                                    {previewPageDescription ? (
                                        <p className="text-slate-600 md:text-md dark:text-slate-400 text-sm text-pretty leading-snug">
                                            <RichLabel>{previewPageDescription}</RichLabel>
                                        </p>
                                    ) : null}
                                </header>
                            ) : null}
                            {previewInstructions ? (
                                <p className="mt-2 mb-0 font-medium text-slate-700 md:text-md dark:text-slate-400 text-xs sm:text-sm text-left italic leading-snug tracking-wide">
                                    <RichLabel>{previewInstructions}</RichLabel>
                                </p>
                            ) : null}
                            <ui.Form {...previewMethods}>
                                <form
                                    noValidate
                                    onSubmit={(event) => {
                                        event.preventDefault();
                                        goNext();
                                    }}
                                    className="space-y-4 mt-3 p-0 text-lg"
                                >
                                    {items.length === 0 ? (
                                        <p className="text-slate-500 text-sm">Add your first question to this block.</p>
                                    ) : (
                                        items.map((item) =>
                                            item.kind === "input" ? (
                                                <div className="mb-4" key={item.id}>
                                                    <InputSwitch
                                                        form={previewMethods}
                                                        input={
                                                            (item.id === selectedInputId && liveEdited
                                                                ? { ...item.input, ...liveEdited }
                                                                : item.input) as unknown as InputT
                                                        }
                                                        is_preview
                                                    />
                                                </div>
                                            ) : (
                                                <div className="mb-4" key={item.id}>
                                                    <BlockSwitch block={item.block} />
                                                </div>
                                            )
                                        )
                                    )}
                                    <div className="flex flex-row-reverse items-center gap-x-4 mb-8 pt-2 w-full">
                                        <div className="w-2/3">
                                            {previewIsLastPage ? <fc.SubmitButton /> : <fc.NextButton />}
                                        </div>
                                        <div className="w-1/3">
                                            <fc.BackButton
                                                className="w-full"
                                                label="Back"
                                                disabled={pageIndex <= 0}
                                                onClick={goBack}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </ui.Form>
                            {previewForm.copyright_info ? (
                                <p className="mt-8 text-muted-foreground text-xs text-center">
                                    {previewForm.copyright_info}
                                </p>
                            ) : null}
                        </>
                    ) : (
                        <p className="text-slate-500 text-xs">
                            {form.form_name || "Untitled form"} - select a page to preview it here.
                        </p>
                    )}
                </div>
            </div>
            <div className="flex justify-center items-center gap-2 bg-background p-2 border-t">
                <div className="flex p-0.5 border rounded-full">
                    <ui.Button
                        variant={device === "mobile" ? "secondary" : "ghost"}
                        size="icon"
                        className="rounded-full w-7 h-7"
                        aria-label="Mobile preview"
                        aria-pressed={device === "mobile"}
                        onClick={() => onDeviceChange("mobile")}
                    >
                        <Smartphone size={16} />
                    </ui.Button>
                    <ui.Button
                        variant={device === "desktop" ? "secondary" : "ghost"}
                        size="icon"
                        className="rounded-full w-7 h-7"
                        aria-label="Desktop preview"
                        aria-pressed={device === "desktop"}
                        onClick={() => onDeviceChange("desktop")}
                    >
                        <Monitor size={16} />
                    </ui.Button>
                </div>
            </div>
        </div>
    );
}

function FormBuilderCanvas({
    form: persistedForm,
    search,
    onSelectionChange,
    tab,
    onTabChange,
}: {
    form?: FormBuilderData;
    search: FormBuilderSearch;
    onSelectionChange: SelectionChange;
    tab?: BuilderTab;
    onTabChange?: (tab: BuilderTab) => void;
}) {
    const workspace = useFormBuilderWorkspace();
    const form = workspace?.form ?? persistedForm!;
    const formId = form.form_id;
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const createForm = useCreateFormFromDraft();
    const upsertPage = useUpsertPage(formId);
    const deletePage = useDeletePage(formId);
    const upsertInput = useUpsertBuilderInput(formId);
    const upsertBlock = useUpsertBuilderBlock(formId);
    const deleteBlock = useDeleteBuilderBlock(formId);

    const [saveError, setSaveError] = useState<string | null>(null);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [focusSelectionId, setFocusSelectionId] = useState<string | null>(null);
    const isWide = useMediaQuery("(min-width: 1280px)");
    const isReadOnly = form.is_published === true || form.is_licensed === true;
    const hasCanonicalizedSearch = useRef(false);
    const selection = resolveFormBuilderSelection(form, search);
    const selectedPage = selection.page;
    const selectedInput = selection.input;
    const selectedBlock = selection.block;

    useEffect(() => {
        // Add page / question / content selects the new row before the
        // form query includes it. Keep that selection until the row is present.
        const waitingForNewItem =
            (search.pageId && !form.pages.some((candidate) => candidate.page_id === search.pageId)) ||
            (search.blockId &&
                !form.pages.some((candidate) => candidate.blocks.some((block) => block.block_id === search.blockId))) ||
            (search.inputId &&
                !form.pages.some((candidate) => candidate.inputs.some((input) => input.input_id === search.inputId)));
        if (waitingForNewItem) return;
        if (
            !hasCanonicalizedSearch.current ||
            search.pageId !== selection.search.pageId ||
            search.inputId !== selection.search.inputId ||
            search.blockId !== selection.search.blockId
        ) {
            hasCanonicalizedSearch.current = true;
            onSelectionChange(selection.search, { replace: true });
        }
    }, [
        onSelectionChange,
        search.blockId,
        search.inputId,
        search.pageId,
        selection.search.blockId,
        selection.search.inputId,
        selection.search.pageId,
    ]);

    // Used to pass the live edited state up to the preview
    const [liveEdited, setLiveEdited] = useState<InputInsert | null>(null);

    useEffect(() => setLiveEdited(null), [selectedInput?.input_id]);

    useEffect(() => {
        if (!focusSelectionId) return;
        const target = document.querySelector<HTMLButtonElement>(`[data-builder-selection-id="${focusSelectionId}"]`);
        if (!target) return;
        target.focus();
        setFocusSelectionId(null);
    }, [focusSelectionId, selection.search.blockId, selection.search.inputId, selection.search.pageId]);

    const handleAddPage = () => {
        const id = crypto.randomUUID();
        const nextIndex = form.pages.length ? Math.max(...form.pages.map((p) => p.page_index)) + 1 : 1;
        upsertPage.mutate(
            {
                page_id: id,
                form_id: formId,
                page_label: null,
                page_index: nextIndex,
            },
            {
                onSuccess: () => {
                    setFocusSelectionId(id);
                    onSelectionChange({ pageId: id });
                },
            }
        );
    };

    const handleDeletePage = (pageId: string) => {
        deletePage.mutate(pageId, {
            onSuccess: () => {
                const index = form.pages.findIndex((page) => page.page_id === pageId);
                const fallback = form.pages[index + 1] ?? form.pages[index - 1];
                setFocusSelectionId(fallback?.page_id ?? null);
                onSelectionChange(fallback ? { pageId: fallback.page_id } : {}, { replace: true });
            },
        });
    };

    const handleAddInput = (pageId: string) => {
        const page = form.pages.find((p) => p.page_id === pageId);
        const nextIndex = page?.inputs.length ? Math.max(...page.inputs.map((i) => i.input_index)) + 1 : 1;
        const id = crypto.randomUUID();
        upsertInput.mutate(
            {
                input_id: id,
                page_id: pageId,
                input_key: "scale-rating",
                input_label: "New Question",
                input_index: nextIndex,
                is_optional: false,
                is_editable: true,
                allow_multiple: false,
                orientation: "vertical",
                scale_options: [
                    { label: "Strongly agree", value: 4 },
                    { label: "Agree", value: 3 },
                    { label: "Neutral", value: 2 },
                    { label: "Disagree", value: 1 },
                    { label: "Strongly disagree", value: 0 },
                ] as unknown as InputInsert["scale_options"],
            },
            {
                onSuccess: () => {
                    setFocusSelectionId(id);
                    onSelectionChange({ pageId, inputId: id });
                },
            }
        );
    };

    const handleAddBlock = (pageId: string) => {
        const page = form.pages.find((p) => p.page_id === pageId);
        const allIndexes = [
            ...(page?.inputs ?? []).map((i) => i.input_index),
            ...(page?.blocks ?? []).map((b) => b.block_index),
        ];
        const nextIndex = allIndexes.length > 0 ? Math.max(...allIndexes) + 1 : 1;
        const id = crypto.randomUUID();
        upsertBlock.mutate(
            {
                block_id: id,
                page_id: pageId,
                block_type: "text",
                block_index: nextIndex,
                content: "New content",
                media_url: null,
                caption: null,
            },
            {
                onSuccess: () => {
                    setFocusSelectionId(id);
                    onSelectionChange({ pageId, blockId: id });
                },
            }
        );
    };

    const totalQuestions = form.pages.reduce((sum, p) => sum + p.inputs.length, 0);

    // ── Formbricks-style state ──────────────────────────────────
    const [localTab, setLocalTab] = useState<BuilderTab>("questions");
    const activeTab = tab ?? localTab;
    const [languageLocale, setLanguageLocale] = useState<SupportedLocale>("de-DE");
    // The Language step previews the form as patients in that language see it.
    const { data: translations } = useFormTranslations(form, { enabled: activeTab === "language" && !workspace });
    const translatedForm = useMemo(() => {
        if (!translations) return form;
        const forLocale = <T extends { locale: string }>(rows: T[]) =>
            rows.filter((row) => row.locale === languageLocale);
        return applyFormTranslations(form as never, languageLocale, {
            form: forLocale(translations.forms)[0] ?? null,
            pages: forLocale(translations.pages),
            inputs: forLocale(translations.inputs) as never,
        }) as unknown as FormBuilderData;
    }, [form, translations, languageLocale]);
    const setActiveTab = (next: BuilderTab) => (onTabChange ? onTabChange(next) : setLocalTab(next));
    const auth = useAuth();
    // Screeners are admin-only: RLS only lets admins write them.
    const isAdmin = Boolean(auth?.roles?.isAdmin || auth?.isSuperUser);
    const { data: screener } = useFormScreener(formId, { enabled: !workspace });
    const [previewDevice, setPreviewDevice] = useState<"mobile" | "desktop">("desktop");
    const [openLogicFor, setOpenLogicFor] = useState<string | null>(null);
    const deleteInput = useDeleteBuilderInput(formId);
    const sortedPages = [...form.pages].sort((a, b) => a.page_index - b.page_index);

    const movePage = (pageId: string, dir: -1 | 1) => {
        const idx = sortedPages.findIndex((p) => p.page_id === pageId);
        const swap = sortedPages[idx + dir];
        const page = sortedPages[idx];
        if (!page || !swap) return;
        upsertPage.mutate({ page_id: page.page_id, form_id: formId, page_index: swap.page_index });
        upsertPage.mutate({ page_id: swap.page_id, form_id: formId, page_index: page.page_index });
    };

    const duplicatePage = (pageId: string) => {
        const page = form.pages.find((p) => p.page_id === pageId);
        if (!page) return;
        const nextIndex = Math.max(...form.pages.map((p) => p.page_index)) + 1;
        const newPageId = crypto.randomUUID();
        upsertPage.mutate(
            {
                page_id: newPageId,
                form_id: formId,
                page_label: `${page.page_label ?? "Page"} (copy)`,
                page_index: nextIndex,
            },
            {
                onSuccess: () => {
                    for (const input of page.inputs) {
                        const { input_id: _omit, ...rest } = input;
                        upsertInput.mutate({ ...rest, input_id: crypto.randomUUID(), page_id: newPageId });
                    }
                    for (const block of page.blocks) {
                        const { block_id: _omitBlock, ...rest } = block;
                        upsertBlock.mutate({ ...rest, block_id: crypto.randomUUID(), page_id: newPageId });
                    }
                    onSelectionChange({ pageId: newPageId });
                },
            }
        );
    };

    const moveItem = (page: BuilderPage, item: ReturnType<typeof getPageItems>[number], dir: -1 | 1) => {
        const items = getPageItems(page);
        const idx = items.findIndex((i) => i.id === item.id);
        const swap = items[idx + dir];
        if (!swap) return;
        queryClient.setQueryData<FormBuilderData>(["form-builder", formId], (current) => {
            if (!current) return current;
            const nextIndex = (id: string, kind: "input" | "block", index: number) =>
                current.pages.map((candidate) => {
                    if (candidate.page_id !== page.page_id) return candidate;
                    if (kind === "input") {
                        return {
                            ...candidate,
                            inputs: candidate.inputs.map((input) =>
                                input.input_id === id ? { ...input, input_index: index } : input
                            ),
                        };
                    }
                    return {
                        ...candidate,
                        blocks: candidate.blocks.map((block) =>
                            block.block_id === id ? { ...block, block_index: index } : block
                        ),
                    };
                });
            const withItem = nextIndex(item.id, item.kind, swap.index);
            return {
                ...current,
                pages: withItem.map((candidate) => {
                    if (candidate.page_id !== page.page_id) return candidate;
                    if (swap.kind === "input") {
                        return {
                            ...candidate,
                            inputs: candidate.inputs.map((input) =>
                                input.input_id === swap.id ? { ...input, input_index: item.index } : input
                            ),
                        };
                    }
                    return {
                        ...candidate,
                        blocks: candidate.blocks.map((block) =>
                            block.block_id === swap.id ? { ...block, block_index: item.index } : block
                        ),
                    };
                }),
            };
        });
        if (item.kind === "input") {
            upsertInput.mutate({
                input_id: item.input.input_id,
                input_key: item.input.input_key,
                page_id: item.input.page_id,
                input_index: swap.index,
            });
        } else {
            upsertBlock.mutate({
                block_id: item.block.block_id,
                page_id: item.block.page_id,
                block_type: item.block.block_type,
                content: item.block.content,
                media_url: item.block.media_url,
                caption: item.block.caption,
                block_index: swap.index,
            });
        }
        if (swap.kind === "input") {
            upsertInput.mutate({
                input_id: swap.input.input_id,
                input_key: swap.input.input_key,
                page_id: swap.input.page_id,
                input_index: item.index,
            });
        } else {
            upsertBlock.mutate({
                block_id: swap.block.block_id,
                page_id: swap.block.page_id,
                block_type: swap.block.block_type,
                content: swap.block.content,
                media_url: swap.block.media_url,
                caption: swap.block.caption,
                block_index: item.index,
            });
        }
    };

    const duplicateItem = (page: BuilderPage, item: ReturnType<typeof getPageItems>[number]) => {
        const allIndexes = [...page.inputs.map((i) => i.input_index), ...page.blocks.map((b) => b.block_index)];
        const nextIndex = allIndexes.length > 0 ? Math.max(...allIndexes) + 1 : 1;
        if (item.kind === "input") {
            const { input_id: _omit, created_at: _created, updated_at: _updated, ...rest } = item.input;
            const inputId = crypto.randomUUID();
            upsertInput.mutate(
                { ...rest, input_id: inputId, input_index: nextIndex },
                {
                    onSuccess: () => {
                        setFocusSelectionId(inputId);
                        onSelectionChange({ pageId: page.page_id, inputId });
                    },
                }
            );
        } else {
            const { block_id: _omitBlock, created_at: _created, last_edited: _edited, ...rest } = item.block;
            const blockId = crypto.randomUUID();
            upsertBlock.mutate(
                { ...rest, block_id: blockId, block_index: nextIndex },
                {
                    onSuccess: () => {
                        setFocusSelectionId(blockId);
                        onSelectionChange({ pageId: page.page_id, blockId });
                    },
                }
            );
        }
    };

    const deleteItem = (page: BuilderPage, item: ReturnType<typeof getPageItems>[number]) => {
        if (item.kind === "input") {
            suppressedQuestionSaves.add(item.input.input_id);
            void flushAutoSaves().finally(() => {
                deleteInput.mutate(item.input.input_id, {
                    onSuccess: () => {
                        if (selectedInput?.input_id === item.id)
                            onSelectionChange({ pageId: page.page_id }, { replace: true });
                    },
                });
            });
        } else {
            deleteBlock.mutate(item.block.block_id, {
                onSuccess: () => {
                    if (selectedBlock?.block_id === item.id)
                        onSelectionChange({ pageId: page.page_id }, { replace: true });
                },
            });
        }
    };

    // ── Formbricks-style layout (no Styling tab) ────────────────
    return (
        <div className="flex flex-col h-[calc(100vh-4rem)]">
            {/* Top bar */}
            <div className="flex items-center gap-3 bg-background px-6 py-3 border-b">
                <BuilderTopTabs
                    active={activeTab}
                    onChange={setActiveTab}
                    enabled={{
                        calculator: form.score_engine != null || (form.hiddenInputs?.length ?? 0) > 0,
                        screener: Boolean(screener?.is_active),
                        chart: form.show_chart !== false,
                    }}
                />
                <div className="flex flex-1 justify-end items-center gap-2">
                    <BuilderSaveStatus formId={formId} />
                    {workspace ? (
                        <>
                            <ui.Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={createForm.isPending}
                                onClick={() => void navigate({ to: "/builder/forms" })}
                            >
                                Discard draft
                            </ui.Button>
                            <ui.Button
                                type="button"
                                size="sm"
                                disabled={createForm.isPending}
                                onClick={() => {
                                    setSaveError(null);
                                    void flushAutoSaves()
                                        .then(() => {
                                            const latest = workspace.getForm();
                                            const draft = formBuilderDataToDraft(latest);
                                            if (!draft) {
                                                setSaveError("The form needs a name and at least one valid question.");
                                                return;
                                            }
                                            createForm.mutate(
                                                {
                                                    draft,
                                                    calculator: latest.score_engine === "sum_total" ? "total" : "none",
                                                    hiddenFields: hiddenFieldsFromInputs(latest.hiddenInputs),
                                                },
                                                {
                                                    onSuccess: ({ formId: savedFormId }) =>
                                                        void navigate({
                                                            to: "/builder/forms/$formId",
                                                            params: { formId: savedFormId },
                                                        }),
                                                    onError: (caught) =>
                                                        setSaveError(
                                                            caught instanceof Error
                                                                ? caught.message
                                                                : "The form draft could not be created."
                                                        ),
                                                }
                                            );
                                        })
                                        .catch((caught) =>
                                            setSaveError(
                                                caught instanceof Error
                                                    ? caught.message
                                                    : "Resolve failed draft saves first."
                                            )
                                        );
                                }}
                            >
                                {createForm.isPending ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
                                Save as draft
                            </ui.Button>
                        </>
                    ) : (
                        <FormPublicationControls form={form} />
                    )}
                    <FormAiAssistant form={form} />
                </div>
            </div>

            {saveError ? (
                <p className="bg-destructive/10 px-4 py-1.5 border-b text-destructive text-xs" role="alert">
                    {saveError}
                </p>
            ) : null}

            {/* Mobile gate */}
            <div className="md:hidden flex flex-col justify-center items-center gap-4 p-8 text-center">
                <div className="flex justify-center items-center bg-muted rounded-full w-16 h-16">
                    <Monitor size={28} className="text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg">Desktop Only</h3>
                <p className="max-w-xs text-muted-foreground text-sm">
                    The form builder requires a wider screen. Please switch to a desktop or laptop to use this tool.
                </p>
            </div>

            {/* Body */}
            <div className="hidden md:flex flex-1 gap-4 p-4 min-h-0 overflow-hidden">
                {/* LEFT — editor column */}
                <div className="flex-1 min-w-0 overflow-y-auto">
                    <div className="space-y-3 pb-16 w-full">
                        {activeTab === "questions" ? (
                            <>
                                {sortedPages.map((page, pageIdx) => {
                                    const items = getPageItems(page);
                                    const questionCount = page.inputs.length;
                                    const isBlockSelected = selectedPage?.page_id === page.page_id;
                                    const logicOpen = openLogicFor === page.page_id;
                                    const pageFocused = isBlockSelected && !selectedInput && !selectedBlock;
                                    return (
                                        <BlockCardShell
                                            key={page.page_id}
                                            index={pageIdx + 1}
                                            title={`Page ${pageIdx + 1}`}
                                            subtitle={[
                                                page.page_label?.trim(),
                                                `${questionCount} question${questionCount !== 1 ? "s" : ""}`,
                                            ]
                                                .filter(Boolean)
                                                .join(" · ")}
                                            selected={pageFocused}
                                            containsSelection={isBlockSelected && !pageFocused}
                                            onSelect={() => onSelectionChange({ pageId: page.page_id })}
                                            onMoveUp={() => movePage(page.page_id, -1)}
                                            onMoveDown={() => movePage(page.page_id, 1)}
                                            onDuplicate={() => duplicatePage(page.page_id)}
                                            onDelete={() => handleDeletePage(page.page_id)}
                                            canMoveUp={pageIdx > 0}
                                            canMoveDown={pageIdx < sortedPages.length - 1}
                                            disabled={isReadOnly}
                                        >
                                            <div className="space-y-2">
                                                {/* Clicking the page itself, not a question, edits its details here. */}
                                                {pageFocused ? (
                                                    <div className="border rounded-lg">
                                                        <PageDetailsEditor
                                                            key={page.page_id}
                                                            page={page}
                                                            form={form}
                                                            formId={formId}
                                                        />
                                                    </div>
                                                ) : null}
                                                {items.map((item) => {
                                                    const isItemSelected =
                                                        item.kind === "input"
                                                            ? selectedInput?.input_id === item.id
                                                            : selectedBlock?.block_id === item.id;
                                                    const Icon =
                                                        item.kind === "input"
                                                            ? getTypeConfig(item.input.input_key).icon
                                                            : FileText;
                                                    const itemIdx = items.findIndex((i) => i.id === item.id);
                                                    return (
                                                        <div
                                                            key={item.id}
                                                            className={cn(
                                                                "border rounded-lg",
                                                                isItemSelected &&
                                                                    "border-teal-500/60 ring-1 ring-teal-500/30"
                                                            )}
                                                        >
                                                            <div className="flex items-center gap-2 px-3 py-2.5">
                                                                <button
                                                                    type="button"
                                                                    data-builder-selection-id={item.id}
                                                                    onClick={() =>
                                                                        onSelectionChange(
                                                                            item.kind === "input"
                                                                                ? {
                                                                                      pageId: page.page_id,
                                                                                      inputId: item.id,
                                                                                  }
                                                                                : {
                                                                                      pageId: page.page_id,
                                                                                      blockId: item.id,
                                                                                  }
                                                                        )
                                                                    }
                                                                    className="flex flex-1 items-center gap-2 min-w-0 text-left"
                                                                >
                                                                    <Icon
                                                                        size={16}
                                                                        className="text-muted-foreground shrink-0"
                                                                    />
                                                                    <span className="flex-1 min-w-0 text-sm truncate">
                                                                        <RichLabel>{item.label}</RichLabel>
                                                                    </span>
                                                                    {item.kind === "input" &&
                                                                    !item.input.is_optional ? (
                                                                        <span className="text-2xs text-muted-foreground shrink-0">
                                                                            Required
                                                                        </span>
                                                                    ) : null}
                                                                </button>
                                                                <span className="flex items-center gap-0.5 text-muted-foreground shrink-0">
                                                                    <ui.Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        tooltip={false}
                                                                        className="w-7 h-7"
                                                                        aria-label={`Move ${item.label} up on the page`}
                                                                        disabled={itemIdx <= 0}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            moveItem(page, item, -1);
                                                                        }}
                                                                    >
                                                                        <ArrowUp size={16} />
                                                                    </ui.Button>
                                                                    <ui.Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        tooltip={false}
                                                                        className="w-7 h-7"
                                                                        aria-label={`Move ${item.label} down on the page`}
                                                                        disabled={itemIdx >= items.length - 1}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            moveItem(page, item, 1);
                                                                        }}
                                                                    >
                                                                        <ArrowDown size={16} />
                                                                    </ui.Button>
                                                                    <ui.Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        tooltip={false}
                                                                        className="w-7 h-7"
                                                                        aria-label={`Duplicate ${item.label}`}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            duplicateItem(page, item);
                                                                        }}
                                                                    >
                                                                        <Copy size={16} />
                                                                    </ui.Button>
                                                                    <ui.Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        tooltip={false}
                                                                        className="w-7 h-7"
                                                                        aria-label={`Delete ${item.label}`}
                                                                        onClick={(event: React.MouseEvent) => {
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            deleteItem(page, item);
                                                                        }}
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </ui.Button>
                                                                    <ui.DropdownMenu>
                                                                        <ui.DropdownMenuTrigger asChild>
                                                                            <ui.Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                tooltip={false}
                                                                                className="w-7 h-7"
                                                                                aria-label={`More options for ${item.label}`}
                                                                                onClick={(event: React.MouseEvent) =>
                                                                                    event.stopPropagation()
                                                                                }
                                                                            >
                                                                                <Ellipsis size={16} />
                                                                            </ui.Button>
                                                                        </ui.DropdownMenuTrigger>
                                                                        <ui.DropdownMenuContent align="end">
                                                                            <ui.DropdownMenuItem
                                                                                className="cursor-pointer"
                                                                                disabled={itemIdx <= 0}
                                                                                onSelect={() =>
                                                                                    moveItem(page, item, -1)
                                                                                }
                                                                            >
                                                                                Move up
                                                                            </ui.DropdownMenuItem>
                                                                            <ui.DropdownMenuItem
                                                                                className="cursor-pointer"
                                                                                disabled={itemIdx >= items.length - 1}
                                                                                onSelect={() => moveItem(page, item, 1)}
                                                                            >
                                                                                Move down
                                                                            </ui.DropdownMenuItem>
                                                                            <ui.DropdownMenuItem
                                                                                className="cursor-pointer"
                                                                                onSelect={() =>
                                                                                    duplicateItem(page, item)
                                                                                }
                                                                            >
                                                                                Duplicate
                                                                            </ui.DropdownMenuItem>
                                                                            <ui.DropdownMenuItem
                                                                                className="cursor-pointer"
                                                                                onSelect={() => deleteItem(page, item)}
                                                                            >
                                                                                Delete
                                                                            </ui.DropdownMenuItem>
                                                                        </ui.DropdownMenuContent>
                                                                    </ui.DropdownMenu>
                                                                </span>
                                                            </div>
                                                            {isItemSelected ? (
                                                                <div className="border-t max-h-96 overflow-y-auto">
                                                                    {item.kind === "input" && selectedInput ? (
                                                                        <InputConfigPanel
                                                                            key={selectedInput.input_id}
                                                                            input={selectedInput}
                                                                            form={form}
                                                                            formId={formId}
                                                                            onDeleted={() =>
                                                                                onSelectionChange(
                                                                                    { pageId: page.page_id },
                                                                                    { replace: true }
                                                                                )
                                                                            }
                                                                            onEditChange={setLiveEdited}
                                                                        />
                                                                    ) : item.kind === "block" && selectedBlock ? (
                                                                        <BlockConfigPanel
                                                                            key={selectedBlock.block_id}
                                                                            block={selectedBlock}
                                                                            formId={formId}
                                                                            onDeleted={() =>
                                                                                onSelectionChange(
                                                                                    { pageId: page.page_id },
                                                                                    { replace: true }
                                                                                )
                                                                            }
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    className="flex justify-center items-center gap-1 bg-white hover:bg-slate-50 px-3 py-2 border border-slate-300 rounded-md w-full font-medium text-slate-900 text-sm"
                                                    onClick={() => handleAddInput(page.page_id)}
                                                >
                                                    <Plus size={16} /> Add question to page
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex justify-center items-center gap-1 bg-white hover:bg-slate-50 px-3 py-2 border border-slate-300 rounded-md w-full font-medium text-slate-900 text-sm"
                                                    onClick={() => handleAddBlock(page.page_id)}
                                                >
                                                    <Plus size={16} /> Add content to page
                                                </button>
                                                <div className="space-y-1 pt-1">
                                                    <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                                                        <span>Conditional Logic</span>
                                                        <Split size={14} />
                                                    </div>
                                                    <ui.Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-xs"
                                                        aria-expanded={logicOpen}
                                                        onClick={() => setOpenLogicFor(logicOpen ? null : page.page_id)}
                                                    >
                                                        {logicOpen ? "Hide logic" : "Add logic"}{" "}
                                                        <Plus size={16} className="ml-1" />
                                                    </ui.Button>
                                                    {logicOpen ? (
                                                        <div className="p-3 border rounded-lg">
                                                            <PageLogicEditor
                                                                form={form}
                                                                pageId={page.page_id}
                                                                pageLogic={page.page_logic}
                                                                onSave={(logic) =>
                                                                    upsertPage.mutateAsync({
                                                                        page_id: page.page_id,
                                                                        form_id: formId,
                                                                        page_logic: pageLogicToJson(logic),
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </BlockCardShell>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={handleAddPage}
                                    className="flex items-center gap-3 bg-teal-50/50 hover:bg-teal-50 p-4 border border-teal-500/60 border-dashed rounded-xl w-full text-left transition-colors"
                                >
                                    <span className="flex justify-center items-center bg-teal-500 rounded-md w-6 h-6 text-white">
                                        <Plus size={16} />
                                    </span>
                                    <span>
                                        <span className="block font-medium text-sm">Add page</span>
                                        <span className="block text-muted-foreground text-xs">
                                            Choose the first question on your new page
                                        </span>
                                    </span>
                                </button>
                            </>
                        ) : activeTab === "screener" ? (
                            workspace ? (
                                <BuilderComingSoonSection
                                    title="Screener"
                                    description="Save this form before setting it up as a screener."
                                />
                            ) : (
                                <FormScreenerSettings form={form} formId={formId} canEdit={isAdmin} />
                            )
                        ) : activeTab === "calculator" ? (
                            <>
                                <FormScoringSection form={form} formId={formId} />
                                <HiddenFieldsSection form={form} />
                            </>
                        ) : activeTab === "chart" ? (
                            <FormChartSettings form={form} formId={formId} />
                        ) : activeTab === "language" ? (
                            workspace ? (
                                <BuilderComingSoonSection
                                    title="Language"
                                    description="Save this form before adding translations."
                                />
                            ) : (
                                <FormLanguageSettings
                                    form={form}
                                    formId={formId}
                                    canEdit={isAdmin}
                                    locale={languageLocale}
                                    onLocaleChange={setLanguageLocale}
                                />
                            )
                        ) : (
                            <section className="bg-background border rounded-xl overflow-hidden">
                                <div className="px-5 py-4 border-b">
                                    <h2 className="font-semibold text-sm">Form settings</h2>
                                    <p className="text-muted-foreground text-xs">
                                        Name, cover text and clinical metadata.
                                    </p>
                                </div>
                                <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
                                    <FormSettingsSheet form={form} formId={formId} />
                                </div>
                            </section>
                        )}
                    </div>
                </div>

                {/* RIGHT — live preview */}
                <div className="hidden lg:block w-[380px] shrink-0">
                    {activeTab === "questions" || activeTab === "language" ? (
                        <BuilderPreviewPane
                            form={activeTab === "language" ? translatedForm : form}
                            page={selectedPage}
                            device={previewDevice}
                            onDeviceChange={setPreviewDevice}
                            liveEdited={activeTab === "language" ? null : liveEdited}
                            selectedInputId={selectedInput?.input_id}
                            onSelectPage={(pageId) => onSelectionChange({ pageId })}
                        />
                    ) : (
                        <BuilderStepPreview step={activeTab} form={form} screener={screener} />
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile gate */}
            <div className="md:hidden flex flex-col justify-center items-center gap-4 p-8 h-[calc(100vh-4rem)] text-center">
                <div className="flex justify-center items-center bg-muted rounded-full w-16 h-16">
                    <Monitor size={28} className="text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg">Desktop Only</h3>
                <p className="max-w-xs text-muted-foreground text-sm">
                    The form builder requires a wider screen. Please switch to a desktop or laptop to use this tool.
                </p>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:flex gap-3 p-3 h-[calc(100vh-4rem)]">
                {/* LEFT — Sidebar card */}
                <ui.Card className="flex flex-col w-72 lg:w-80 overflow-hidden shrink-0">
                    <ui.CardHeader className="p-3 pb-0">
                        <p className="min-w-0 font-medium text-sm break-words text-pretty">
                            {form.form_name || "Untitled Form"}
                        </p>
                    </ui.CardHeader>

                    <ui.CardContent className="flex-1 p-0 overflow-hidden">
                        <ScrollArea
                            className="h-full"
                            viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
                        >
                            <div className="space-y-0.5 p-2 min-w-0">
                                {form.pages.map((page, pageIdx) => {
                                    const isExpanded = selectedPage?.page_id === page.page_id;
                                    const mergedPageItems = getPageItems(page);
                                    return (
                                        <div key={page.page_id}>
                                            <div className="group flex items-center">
                                                <button
                                                    type="button"
                                                    data-builder-selection-id={page.page_id}
                                                    aria-label={page.page_label ?? `Page ${pageIdx + 1}`}
                                                    aria-current={isExpanded ? "page" : undefined}
                                                    aria-expanded={isExpanded}
                                                    title={page.page_label ?? `Page ${pageIdx + 1}`}
                                                    className={cn(
                                                        "flex flex-1 items-center gap-1 px-2 py-1.5 rounded-md min-w-0 text-sm text-left leading-5",
                                                        isExpanded ? "bg-primary/10 text-primary" : "hover:bg-muted"
                                                    )}
                                                    onClick={() => onSelectionChange({ pageId: page.page_id })}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDown
                                                            size={16}
                                                            className="text-muted-foreground shrink-0"
                                                        />
                                                    ) : (
                                                        <ChevronRight
                                                            size={16}
                                                            className="text-muted-foreground shrink-0"
                                                        />
                                                    )}
                                                    <span className="font-medium text-muted-foreground shrink-0">
                                                        {pageIdx + 1}.
                                                    </span>
                                                    <span className="flex-1 min-w-0 font-medium break-words text-pretty">
                                                        <RichLabel>{page.page_label ?? "Untitled"}</RichLabel>
                                                    </span>
                                                </button>
                                            </div>

                                            {isExpanded ? (
                                                <div className="space-y-0.5 ml-5 py-0.5 pl-3 border-l min-w-0">
                                                    {mergedPageItems.map((item, itemIdx) => {
                                                        const isSelected =
                                                            item.kind === "input"
                                                                ? selectedInput?.input_id === item.id
                                                                : selectedBlock?.block_id === item.id;
                                                        const Icon =
                                                            item.kind === "input"
                                                                ? getTypeConfig(item.input.input_key).icon
                                                                : FileText;
                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className={cn(
                                                                    "flex items-center rounded-md w-full min-w-0 text-sm leading-5 transition-colors",
                                                                    isSelected
                                                                        ? "bg-primary/10 text-primary font-medium"
                                                                        : "hover:bg-muted text-muted-foreground"
                                                                )}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    data-builder-selection-id={item.id}
                                                                    aria-current={isSelected ? "true" : undefined}
                                                                    title={item.label}
                                                                    className="flex flex-1 items-center gap-1.5 py-1 pl-2 min-w-0 text-left"
                                                                    onClick={() => {
                                                                        onSelectionChange(
                                                                            item.kind === "input"
                                                                                ? {
                                                                                      pageId: page.page_id,
                                                                                      inputId: item.id,
                                                                                  }
                                                                                : {
                                                                                      pageId: page.page_id,
                                                                                      blockId: item.id,
                                                                                  }
                                                                        );
                                                                        setLiveEdited(null);
                                                                    }}
                                                                >
                                                                    <Icon size={16} className="opacity-60 shrink-0" />
                                                                    <span className="flex-1 min-w-0 text-xs break-words text-pretty">
                                                                        <RichLabel>{item.label}</RichLabel>
                                                                    </span>
                                                                </button>
                                                                <div className="flex pr-1 shrink-0">
                                                                    {itemIdx > 0 && (
                                                                        <ui.Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="w-5 h-5"
                                                                            disabled={
                                                                                isReadOnly ||
                                                                                upsertInput.isPending ||
                                                                                upsertBlock.isPending
                                                                            }
                                                                            aria-label={`Move ${item.label} up`}
                                                                            onClick={(e: React.MouseEvent) => {
                                                                                e.stopPropagation();
                                                                                const swap =
                                                                                    mergedPageItems[itemIdx - 1];
                                                                                if (!swap) return;
                                                                                if (item.kind === "input") {
                                                                                    upsertInput.mutate({
                                                                                        input_id: item.input.input_id,
                                                                                        input_key: item.input.input_key,
                                                                                        input_index: swap.index,
                                                                                        page_id: item.input.page_id,
                                                                                    });
                                                                                } else {
                                                                                    upsertBlock.mutate({
                                                                                        block_id: item.block.block_id,
                                                                                        page_id: item.block.page_id,
                                                                                        block_type:
                                                                                            item.block.block_type,
                                                                                        content: item.block.content,
                                                                                        media_url: item.block.media_url,
                                                                                        caption: item.block.caption,
                                                                                        block_index: swap.index,
                                                                                    });
                                                                                }
                                                                                if (swap.kind === "input") {
                                                                                    upsertInput.mutate({
                                                                                        input_id: swap.input.input_id,
                                                                                        input_key: swap.input.input_key,
                                                                                        input_index: item.index,
                                                                                        page_id: swap.input.page_id,
                                                                                    });
                                                                                } else {
                                                                                    upsertBlock.mutate({
                                                                                        block_id: swap.block.block_id,
                                                                                        page_id: swap.block.page_id,
                                                                                        block_type:
                                                                                            swap.block.block_type,
                                                                                        content: swap.block.content,
                                                                                        media_url: swap.block.media_url,
                                                                                        caption: swap.block.caption,
                                                                                        block_index: item.index,
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            <ChevronUp size={20} />
                                                                        </ui.Button>
                                                                    )}
                                                                    {itemIdx < mergedPageItems.length - 1 && (
                                                                        <ui.Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="w-5 h-5"
                                                                            disabled={
                                                                                isReadOnly ||
                                                                                upsertInput.isPending ||
                                                                                upsertBlock.isPending
                                                                            }
                                                                            aria-label={`Move ${item.label} down`}
                                                                            onClick={(e: React.MouseEvent) => {
                                                                                e.stopPropagation();
                                                                                const swap =
                                                                                    mergedPageItems[itemIdx + 1];
                                                                                if (!swap) return;
                                                                                if (item.kind === "input") {
                                                                                    upsertInput.mutate({
                                                                                        input_id: item.input.input_id,
                                                                                        input_key: item.input.input_key,
                                                                                        input_index: swap.index,
                                                                                        page_id: item.input.page_id,
                                                                                    });
                                                                                } else {
                                                                                    upsertBlock.mutate({
                                                                                        block_id: item.block.block_id,
                                                                                        page_id: item.block.page_id,
                                                                                        block_type:
                                                                                            item.block.block_type,
                                                                                        content: item.block.content,
                                                                                        media_url: item.block.media_url,
                                                                                        caption: item.block.caption,
                                                                                        block_index: swap.index,
                                                                                    });
                                                                                }
                                                                                if (swap.kind === "input") {
                                                                                    upsertInput.mutate({
                                                                                        input_id: swap.input.input_id,
                                                                                        input_key: swap.input.input_key,
                                                                                        input_index: item.index,
                                                                                        page_id: swap.input.page_id,
                                                                                    });
                                                                                } else {
                                                                                    upsertBlock.mutate({
                                                                                        block_id: swap.block.block_id,
                                                                                        page_id: swap.block.page_id,
                                                                                        block_type:
                                                                                            swap.block.block_type,
                                                                                        content: swap.block.content,
                                                                                        media_url: swap.block.media_url,
                                                                                        caption: swap.block.caption,
                                                                                        block_index: item.index,
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            <ChevronDown size={20} />
                                                                        </ui.Button>
                                                                    )}
                                                                    {item.kind === "block" ? (
                                                                        <ui.Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="w-5 h-5"
                                                                            disabled={
                                                                                isReadOnly || deleteBlock.isPending
                                                                            }
                                                                            aria-label={`Delete ${item.label}`}
                                                                            onClick={(e: React.MouseEvent) => {
                                                                                e.stopPropagation();
                                                                                deleteBlock.mutate(
                                                                                    item.block.block_id,
                                                                                    {
                                                                                        onSuccess: () => {
                                                                                            if (
                                                                                                selectedBlock?.block_id ===
                                                                                                item.block.block_id
                                                                                            ) {
                                                                                                setFocusSelectionId(
                                                                                                    page.page_id
                                                                                                );
                                                                                                onSelectionChange(
                                                                                                    {
                                                                                                        pageId: page.page_id,
                                                                                                    },
                                                                                                    { replace: true }
                                                                                                );
                                                                                            }
                                                                                        },
                                                                                    }
                                                                                );
                                                                            }}
                                                                        >
                                                                            <Trash2 size={20} />
                                                                        </ui.Button>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    <button
                                                        type="button"
                                                        className="flex items-center gap-1 hover:bg-muted px-2 py-1 rounded-md w-full text-muted-foreground hover:text-foreground text-xs transition-colors"
                                                        onClick={() => handleAddInput(page.page_id)}
                                                        disabled={isReadOnly || upsertInput.isPending}
                                                    >
                                                        <Plus size={20} /> Add question
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="flex items-center gap-1 hover:bg-muted px-2 py-1 rounded-md w-full text-muted-foreground hover:text-foreground text-xs transition-colors"
                                                        onClick={() => handleAddBlock(page.page_id)}
                                                        disabled={isReadOnly || upsertBlock.isPending}
                                                    >
                                                        <Plus size={20} /> Add content
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </ui.CardContent>

                    <ui.CardFooter className="p-2 pt-0">
                        <ui.Button
                            variant="outline"
                            size="sm"
                            className="w-full h-8 text-xs"
                            onClick={handleAddPage}
                            disabled={upsertPage.isPending || isReadOnly}
                        >
                            <Plus size={20} className="mr-1" /> Add Page
                        </ui.Button>
                    </ui.CardFooter>
                </ui.Card>

                {/* CENTRE — Live preview card */}
                <ui.Card className="flex flex-col flex-1 min-w-0">
                    <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 p-3">
                        <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-xs">
                                {form.pages.length} page{form.pages.length !== 1 ? "s" : ""}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                                {totalQuestions} question{totalQuestions !== 1 ? "s" : ""}
                            </Badge>
                            <BuilderSaveStatus formId={formId} />
                            {isReadOnly ? (
                                <Badge variant="secondary" className="text-xs">
                                    Read-only
                                </Badge>
                            ) : null}
                            {saveError ? (
                                <p className="text-2xs text-destructive" role="alert">
                                    {saveError}
                                </p>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                            {!isWide && selectedPage ? (
                                <ui.Sheet open={inspectorOpen} onOpenChange={setInspectorOpen}>
                                    <ui.SheetTrigger asChild>
                                        <ui.Button type="button" variant="outline" size="sm">
                                            <Settings size={16} /> Inspector
                                        </ui.Button>
                                    </ui.SheetTrigger>
                                    <ui.SheetContent className="flex flex-col gap-0 p-0 w-full sm:max-w-md overflow-hidden">
                                        <ui.SheetHeader className="px-5 py-4 border-b text-left">
                                            <ui.SheetTitle>
                                                {selectedInput
                                                    ? "Question settings"
                                                    : selectedBlock
                                                      ? "Content settings"
                                                      : "Page settings"}
                                            </ui.SheetTitle>
                                        </ui.SheetHeader>
                                        <SelectionInspector
                                            form={form}
                                            page={selectedPage}
                                            input={selectedInput}
                                            block={selectedBlock}
                                            readOnly={isReadOnly}
                                            onDeleted={() => {
                                                setFocusSelectionId(selectedPage.page_id);
                                                onSelectionChange({ pageId: selectedPage.page_id }, { replace: true });
                                                setInspectorOpen(false);
                                            }}
                                            onEditChange={setLiveEdited}
                                            onSavePageLogic={(logic) =>
                                                upsertPage.mutateAsync({
                                                    page_id: selectedPage.page_id,
                                                    form_id: formId,
                                                    page_logic: pageLogicToJson(logic),
                                                })
                                            }
                                            onDeletePage={() => handleDeletePage(selectedPage.page_id)}
                                        />
                                    </ui.SheetContent>
                                </ui.Sheet>
                            ) : null}
                            {workspace ? (
                                <>
                                    <ui.Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={createForm.isPending}
                                        onClick={() => void navigate({ to: "/builder/forms" })}
                                    >
                                        Discard draft
                                    </ui.Button>
                                    <ui.Button
                                        type="button"
                                        size="sm"
                                        disabled={createForm.isPending}
                                        onClick={() => {
                                            setSaveError(null);
                                            void flushAutoSaves()
                                                .then(() => {
                                                    const latest = workspace.getForm();
                                                    const draft = formBuilderDataToDraft(latest);
                                                    if (!draft) {
                                                        setSaveError(
                                                            "The form needs a name and at least one valid question."
                                                        );
                                                        return;
                                                    }
                                                    createForm.mutate(
                                                        {
                                                            draft,
                                                            calculator:
                                                                latest.score_engine === "sum_total" ? "total" : "none",
                                                        },
                                                        {
                                                            onSuccess: ({ formId: savedFormId }) =>
                                                                void navigate({
                                                                    to: "/builder/forms/$formId",
                                                                    params: { formId: savedFormId },
                                                                }),
                                                            onError: (caught) =>
                                                                setSaveError(
                                                                    caught instanceof Error
                                                                        ? caught.message
                                                                        : "The form draft could not be created."
                                                                ),
                                                        }
                                                    );
                                                })
                                                .catch((caught) =>
                                                    setSaveError(
                                                        caught instanceof Error
                                                            ? caught.message
                                                            : "Resolve failed draft saves first."
                                                    )
                                                );
                                        }}
                                    >
                                        {createForm.isPending ? (
                                            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                                        ) : null}
                                        Save draft
                                    </ui.Button>
                                </>
                            ) : (
                                <FormPublicationControls form={form} />
                            )}
                            <FormAiAssistant form={form} />
                            <Dialog>
                                <DialogTrigger asChild>
                                    <ui.Button
                                        variant="ghost"
                                        size="icon"
                                        className="w-8 h-8"
                                        disabled={isReadOnly}
                                        aria-label="Form settings"
                                    >
                                        <Settings size={20} />
                                    </ui.Button>
                                </DialogTrigger>
                                <DialogContent className="flex flex-col p-0 max-w-lg max-h-[85vh] overflow-hidden">
                                    <DialogHeader className="p-4 border-b shrink-0">
                                        <DialogTitle>Form Settings</DialogTitle>
                                    </DialogHeader>
                                    <FormSettingsSheet form={form} formId={formId} />
                                </DialogContent>
                            </Dialog>
                        </div>
                    </ui.CardHeader>

                    <ui.CardContent className="flex-1 p-0 overflow-hidden">
                        <PagePreview
                            page={selectedPage}
                            selectedInputId={selectedInput?.input_id}
                            selectedBlockId={selectedBlock?.block_id}
                            liveEdited={liveEdited}
                            onSelect={onSelectionChange}
                        />
                    </ui.CardContent>
                </ui.Card>

                {/* RIGHT — Config panel card */}
                {isWide && selectedPage ? (
                    <ui.Card className="w-[360px] overflow-hidden shrink-0">
                        <SelectionInspector
                            form={form}
                            page={selectedPage}
                            input={selectedInput}
                            block={selectedBlock}
                            readOnly={isReadOnly}
                            onDeleted={() => {
                                setFocusSelectionId(selectedPage.page_id);
                                onSelectionChange({ pageId: selectedPage.page_id }, { replace: true });
                            }}
                            onEditChange={setLiveEdited}
                            onSavePageLogic={(logic) =>
                                upsertPage.mutateAsync({
                                    page_id: selectedPage.page_id,
                                    form_id: formId,
                                    page_logic: pageLogicToJson(logic),
                                })
                            }
                            onDeletePage={() => handleDeletePage(selectedPage.page_id)}
                        />
                    </ui.Card>
                ) : null}
            </div>
        </>
    );
}
